import type { ParticleRenderItem } from "@playsrc/particle"
import { sourceHorizontal4By3FovToVertical } from "@playsrc/rendering"
import type { Camera, Effect, ModelEyeState, ModelLightingInput, ModelLocalLight } from "@playsrc/rendering"
import type { ModelItem } from "@playsrc/rendering"
import type { PresentationArtifacts } from "./artifacts"
import { tf2ClassPresentation, type Tf2ClassPresentation } from "./class"
import type { Tf2EquippedItem } from "./equipment/types"
import type { Snapshot, ActorCloakState, EntityPresentation } from "./codec"
import { configuredEquipmentSounds, nativeEquipmentSounds } from "./equipment/audio.generated"

/** Static skin inputs do not create a prop animation. Keep their template bounds;
 * only animated props replace those bounds with the authored sequence bounds. */
export function studioModelFrameState(presentation: EntityPresentation, identity: number) {
  const state = presentation.studioModels.find(model => model.sourceIndex === identity)
  if (!state) throw new Error(`Studio model state unavailable: ${identity}`)
  const animation = presentation.studioAnimations.find(animation => animation.sourceIndex === identity)
  return Object.freeze({ position: state.worldPosition, angles: state.worldAngles, skin: state.skin,
    renderBounds: animation?.bounds })
}

const UINT32_MAX = 0xffff_ffff
const NORMAL_TOLERANCE = 1e-4
const TF2_DEFAULT_HORIZONTAL_FOV_4_BY_3 = 75
const SOURCE_WORLD_NEAR = 7
const SOURCE_MAP_EXTENT = 16_384
const SOURCE_MAP_EXTENT_DIAGONAL = Math.fround(1.73205080757)
const UTF8_ENCODER = new TextEncoder()
const UTF8_DECODER = new TextDecoder("utf-8", { fatal: true })
const EMPTY_POSE_VALUES: readonly never[] = Object.freeze([])
const POSE_TEXT_CACHE_LIMIT = 256
const POSE_TEXT_CACHE_MAXIMUM_BYTES = 1024
const POSE_TEXT_CACHE = new Map<string, Uint8Array>()

function poseText(value: string): Uint8Array {
  const cached = POSE_TEXT_CACHE.get(value)
  if (cached !== undefined) return cached
  const encoded = UTF8_ENCODER.encode(value)
  if (encoded.byteLength <= POSE_TEXT_CACHE_MAXIMUM_BYTES) {
    if (POSE_TEXT_CACHE.size === POSE_TEXT_CACHE_LIMIT) POSE_TEXT_CACHE.delete(POSE_TEXT_CACHE.keys().next().value!)
    POSE_TEXT_CACHE.set(value, encoded)
  }
  return encoded
}

export type PresentationDiagnostic = Readonly<{
  code: "MissingProjectileModel" | "MissingParticleContext" | "MissingAudioContext"
  identity: string
}>

export type Tf2Hud = Readonly<{
  health: number
  maxHealth: number
  className: Tf2ClassPresentation["displayName"]

  weaponName: "Rocket Launcher" | "Original" | "Stickybomb Launcher" | "Scattergun" | "Pistol" | "Bat" | "Shotgun" | "Shovel" | "Minigun" | "Fists" | "Sniper Rifle" | "SMG" | "Kukri" | "Wrench" | "Flamethrower" | "Fire Axe" | "Bottle" | "Grenade Launcher" | "Construction PDA" | "Destruction PDA" | "Toolbox" | "Revolver" | "Knife" | "Sapper" | "Disguise Kit" | "Invisibility Watch" | "Syringe Gun" | "Medi Gun" | "Bonesaw" | null

  speed: number
  projectileCount: number
}>

export type Tf2AudioRequest = Readonly<{
  action?: Snapshot["audioEvents"][number]["action"]
  fadeSeconds?: number
  tick?: bigint
  voiceIdentity: number

  definition: string
  source: Readonly<{
    kind: "entity" | "world" | "local-listener"
    identity: number
    ownerIdentity: number | null
    origin: readonly [number, number, number]
    radius: number
    sourceClass: string
  }>
  samples: Readonly<{ volume: number; pitch: number; wave: number; soundLevel: number }>
  overrides?: Readonly<{ volume?: number; pitch?: number; channel?: 6 }>
}>

export function tf2Audio(snapshot: Snapshot): readonly Tf2AudioRequest[] {
  return Object.freeze(snapshot.audioEvents.map((event) => Object.freeze({
    action: event.action,
    fadeSeconds: event.fadeSeconds,
    tick: event.tick,
    voiceIdentity: stable32(`${event.tick}:${event.ordinal}:${event.definition}:${event.sourceIdentity}`),
    definition: nativeEquipmentSounds[event.definition] ?? configuredEquipmentSounds[event.definition - 160]!,
    source: Object.freeze({
      kind: event.sourceKind === 3 ? "local-listener" as const : event.sourceKind === 1 || event.sourceKind === 4 || event.sourceKind === 5 ? "entity" as const : "world" as const,
      identity: event.sourceIdentity,
      ownerIdentity: event.ownerIdentity,
      origin: Object.freeze([...event.position]) as Vector3,
      radius: 0,
      sourceClass: event.sourceKind === 5 ? "tf_projectile" : event.identity === 6 ? "worldspawn" : event.identity === 5 || event.sourceKind === 3 ? "player" : event.sourceKind === 4 ? "team_control_point" : event.identity === 3 || event.identity === 4 ? "item" : event.sourceKind === 1
        ? event.sourceIdentity === 1 || snapshot.bots.some(bot => bot.identity === event.sourceIdentity) ? "tf_player" : "tf_weapon"
        : "tf_projectile",
    }),
    samples: event.samples,
    ...(event.pitchOverride === undefined ? {} : { overrides: Object.freeze({ pitch: event.pitchOverride }) }),
    ...(event.volumeOverride === undefined ? {} : { overrides: Object.freeze({ volume: event.volumeOverride, ...(event.identity === 6 ? { channel: 6 as const } : {}) }) }),
  })))
}

export function createMeleeConditionPresenter() {
  let previousTick = -1n
  const running = new Set<number>()
  return Object.freeze({
    map(snapshot: Snapshot): readonly ProjectileParticleRequest[] {
      if (snapshot.tick < previousTick) running.clear()
      previousTick = snapshot.tick
      const requests: ProjectileParticleRequest[] = [], desired = new Set<number>()
      for (const actor of snapshot.bots) {
        const has = (condition: number) => ((actor.conditions[condition >>> 5] ?? 0) & (1 << (condition & 31))) !== 0
        if (actor.lifecycle !== 1 || ![30,48,119].some(has) || [3,4,64,66].some(has)) continue
        desired.add(actor.identity)
        const effectIdentity = `condition:${actor.identity}:mark_for_death`, identity = `${snapshot.tick}:${effectIdentity}`
        const controlPoint = Object.freeze({ index: 0 as const, position: Object.freeze([actor.position[0], actor.position[1], actor.position[2] + actor.overheadHeight] as const),
          orientation: sourceViewOrientation(0, actor.yawDegrees), ownerIdentity: actor.identity })
        if (!running.has(actor.identity)) {
          requests.push(Object.freeze({ kind: "start", identity, effectIdentity, eventIdentity: identity, tick: snapshot.tick,
            projectileIdentity: actor.identity, ownerIdentity: actor.identity, launcherIdentity: actor.identity,
            team: actor.team === 2 ? "red" : "blue", system: "mark_for_death", attachment: null, controlPoints: Object.freeze([controlPoint]) }))
          running.add(actor.identity)
        } else requests.push(Object.freeze({ kind: "set-control-point", identity, effectIdentity, eventIdentity: identity,
          tick: snapshot.tick, projectileIdentity: actor.identity, controlPoint }))
      }
      for (const actor of running) if (!desired.has(actor)) {
        const effectIdentity = `condition:${actor}:mark_for_death`, identity = `${snapshot.tick}:${effectIdentity}:stop`
        requests.push(Object.freeze({ kind: "stop", identity, effectIdentity, eventIdentity: identity, tick: snapshot.tick,
          projectileIdentity: actor, immediate: true }))
        running.delete(actor)
      }
      return Object.freeze(requests)
    },
  })
}

export function projectileFrame(snapshot: Snapshot): ProjectileFrame {
  return Object.freeze({
    ticks: Object.freeze(snapshot.projectileTimeline.map((entry) => Object.freeze({
      tick: entry.tick,
      projectiles: Object.freeze(entry.projectiles.map((projectile) => Object.freeze({
        identity: projectile.identity,
        kind: projectile.kind === 1 ? "rocket" : projectile.kind === 2 ? "sticky" : projectile.kind === 3 ? "syringe" : projectile.kind === 5 ? "grenade" : "flare",
        weapon: projectile.weapon,
        critical: projectile.critical,
        trail: projectile.trail,
        miniRocket: projectile.miniRocket,
        practiceExplosion: projectile.practiceExplosion,
        selfBlastOnly: projectile.selfBlastOnly,
        modelVisible: projectile.modelVisible,
        airBurst: projectile.airBurst,
        underwaterExplosion: projectile.underwaterExplosion,
        team: projectile.team === 2 ? "red" : "blue",
        ownerIdentity: projectile.ownerIdentity,
        launcherIdentity: projectile.launcherIdentity,
        state: projectile.state === 1 ? "flying" : projectile.state === 2 ? "stuck-unarmed" : "stuck-armed",
        position: vector(projectile.position),
        velocity: vector(projectile.velocity),
        orientation: quat(projectile.orientation),
        angularVelocity: vector(projectile.angularVelocity),
        contactNormal: projectile.contactNormal === null ? null : vector(projectile.contactNormal),
        ageSeconds: projectile.ageSeconds,
      }))),
      events: projectileTimelineEvents(entry.events),
    }))),
  })
}

function projectileTimelineEvents(
  events: Snapshot["projectileEvents"],
): readonly ProjectileEvent[] {
  const output: ProjectileEvent[] = []
  const nextKind = new Array<Snapshot["projectileEvents"][number]["type"] | undefined>(events.length)
  const upcoming = new Map<number, Snapshot["projectileEvents"][number]["type"]>()
  for (let index = events.length - 1; index >= 0; index -= 1) {
    const event = events[index]!
    nextKind[index] = upcoming.get(event.projectile)
    upcoming.set(event.projectile, event.type)
  }
  const append = (
    event: Snapshot["projectileEvents"][number],
    kind: ProjectileEventKind,
    sourceEventOrdinal: number,
  ) => {
    output.push(Object.freeze({
      kind,
      projectileKind: event.kind === 1 ? "rocket" : event.kind === 2 ? "sticky" : event.kind === 3 ? "syringe" : event.kind === 5 ? "grenade" : "flare",
      weapon: event.weapon,
      critical: event.critical,
      trail: event.trail,
      miniRocket: event.miniRocket,
      practiceExplosion: event.practiceExplosion,
      selfBlastOnly: event.selfBlastOnly,
      airBurst: event.airBurst,
      underwaterExplosion: event.underwaterExplosion,
      projectileIdentity: event.projectile,
      ownerIdentity: event.ownerIdentity,
      launcherIdentity: event.launcherIdentity,
      team: event.team === 2 ? "red" : "blue",
      tick: event.tick,
      sourceOrdinal: output.length,
      sourceEventOrdinal,
      position: vector(event.position),
      orientation: quat(event.orientation),
      contactNormal: event.contactNormal === null ? null : vector(event.contactNormal),
    }))
  }
  for (let sourceEventOrdinal = 0; sourceEventOrdinal < events.length; sourceEventOrdinal += 1) {
    const event = events[sourceEventOrdinal]!
    append(event, event.type, sourceEventOrdinal)
    if (event.type === "impact") {
      const outcome = nextKind[sourceEventOrdinal]
      if (outcome !== "stick" && outcome !== "explode" && (event.kind === 2 || event.kind === 5 || event.kind === 4 && event.trail === 5 && outcome !== "fizzle")) {
        append(event, "bounce", sourceEventOrdinal)
      }
    } else if (event.type === "fizzle" || event.type === "explode") {
      append(event, "destroy", sourceEventOrdinal)
    }
  }
  return Object.freeze(output)
}
export function projectileModels(models: readonly ProjectileModelRequest[]): readonly ModelItem[] {
  return Object.freeze(
    models.map((m) =>
      Object.freeze({
        identity: m.identity,
        model: m.model,
        position: m.position,
        orientation: m.orientation,
        scale: 1,
        skin: m.skin,
      }),
    ),
  )
}
export { classPipelinePoseRequests, classPreviewBaseActivity } from "./class-pipeline-preparation"
export { mapPropPipelinePoseRequests } from "./map-prop-pipeline-preparation"

export function createViewmodelPresenter(artifacts: PresentationArtifacts, catalog: readonly import("./equipment/types").Tf2SupportedItem[]) {
  const items = new Map(catalog.map((item) => [item.item.definitionIndex, item]))
  let actionTick = 0n
  let priorTick = 0n
  let prior: Snapshot["weapon"] | undefined
  let priorClass: Snapshot["class"] | undefined
  let priorDefinition: number | undefined
  let activity = "ACT_VM_DRAW"
  let phase: 0 | 1 | 2 | 3 | 4 | 5 = 0
  return Object.freeze({
    updateArtifacts(next: PresentationArtifacts): void { artifacts = next },
    map(snapshot: Snapshot, view: Readonly<{ aspectRatio: number; farPlane: number }> = Object.freeze({ aspectRatio: 4 / 3, farPlane: 32768 })): Readonly<{ item: ModelItem; request: ModelPoseRequest; standalone: boolean }> {
      if (snapshot.weapon === null || ![1, 2, 3, 4, 5, 6, 7, 8, 9].includes(snapshot.class) || snapshot.weapon === 54) {
        throw new ProjectilePresentationError("MalformedFact", "class has no implemented viewmodel weapon")
      }
      const instance = snapshot.equippedItems.find((instance) => items.get(instance.definitionIndex)?.classSlots.some((slot) => slot.class === snapshot.class && slot.slot === instance.slot && slot.weapon === snapshot.weapon))
      const equipped = instance && items.get(instance.definitionIndex)
      if (!equipped) throw new ProjectilePresentationError("MalformedFact", "active weapon has no equipped catalog definition")
      const standalone = !equipped.attachToHands
      const itemIdentity = equipped.modelPlayer || undefined
      const identity = standalone ? itemIdentity : tf2ClassPresentation(snapshot.class).hands
      if (!identity) throw new ProjectilePresentationError("MissingModel", "standalone equipped model")
      const artifact = artifacts.models.get(identity)
      const itemArtifact = standalone || itemIdentity === undefined ? undefined : artifacts.models.get(itemIdentity)
      if (!artifact) throw new ProjectilePresentationError("MissingModel", identity)
      if (!standalone && itemIdentity !== undefined && !itemArtifact) throw new ProjectilePresentationError("MissingModel", itemIdentity)
      if (artifact.descriptor.kind !== "viewmodel") throw new ProjectilePresentationError("MissingModel", `${identity}:descriptor`)
      if (itemArtifact && itemArtifact.descriptor.kind !== "viewmodel") throw new ProjectilePresentationError("MissingModel", `${itemIdentity}:descriptor`)
      const weapon = snapshot.loadout.find((value) => value.weapon === snapshot.weapon)
      if (!weapon) throw new ProjectilePresentationError("MissingModel", `${identity}:weapon-state`)
      const selectionChanged = prior !== snapshot.weapon || priorClass !== snapshot.class || priorDefinition !== equipped.item.definitionIndex
      const exact = snapshot.activities.filter((event) => event.weapon === snapshot.weapon).at(-1)
      if (exact !== undefined) {
        activity = ["", "ACT_VM_DRAW", "ACT_VM_PRIMARYATTACK", "ACT_RELOAD_START", "ACT_VM_RELOAD", "ACT_RELOAD_FINISH", "ACT_VM_IDLE", "ACT_VM_SECONDARYATTACK", "ACT_VM_SWINGHARD", "ACT_VM_HITCENTER", "ACT_VM_HITLEFT", "ACT_VM_HITRIGHT", "ACT_MP_ATTACK_STAND_PREFIRE", "ACT_MP_ATTACK_STAND_POSTFIRE", "ACT_VM_PULLBACK"][exact.activity]!
        phase = ([0, 0, 1, 2, 3, 4, 5, 1, 1, 1, 1, 1, 1, 1, 1] as const)[exact.activity]!
        actionTick = exact.tick
      } else if (selectionChanged) { activity = "ACT_VM_DRAW"; phase = 0; actionTick = snapshot.tick }
      prior = snapshot.weapon
      priorClass = snapshot.class
      priorDefinition = equipped.item.definitionIndex
      const currentElapsed = Number(snapshot.tick - actionTick) * 0.015
      const previousElapsed = Math.max(0, Number(priorTick - actionTick) * 0.015)
      const frameTime = Math.max(0, Number(snapshot.tick - priorTick) * 0.015)
      priorTick = snapshot.tick
      const now=Number(snapshot.tick)*0.015
      const common = {
        identity: 0x7fff_ff00 + snapshot.class * 4,
        model: identity, activity,
        itemDefinition: equipped.item.definitionIndex, activityStartTick: actionTick, allowIdleTransition: weapon.reload === 0 && (snapshot.weapon !== 3 || weapon.chargeBeginTick === null),
        prefirePlaybackRate: weapon.prefirePlaybackRate ?? undefined,
        previousElapsedSeconds: Math.min(previousElapsed, currentElapsed), elapsedSeconds: currentElapsed,
        currentTimeSeconds: now, frameTimeSeconds: frameTime, planarSpeed: Math.hypot(snapshot.velocity[0], snapshot.velocity[1]),
        screenAspectRatio: view.aspectRatio, worldFarPlane: view.farPlane,
        skin: snapshot.team === 2 ? 0 : 1, lod: 0,
        bodygroups: Object.freeze(artifact.bodygroupCounts.map(() => 0)),
      }
      const composed = standalone ? common : {
        ...common,
        ...(itemIdentity === undefined ? { handsOnlyViewmodel: true } : { itemModel: itemIdentity }),
        phase: phase as 0 | 1 | 2 | 3 | 4 | 5,
        reflectedViewmodel: false, ownerAlive: snapshot.lifecycle === 1,
        ...(itemArtifact ? { itemBodygroups: Object.freeze(itemArtifact.bodygroupCounts.map(() => 0)) } : {}),
      }
      return Object.freeze({
        standalone,
        item: Object.freeze({ identity: common.identity, model: identity, position: Object.freeze([0, 0, 0]) as Vector3,
          angles: Object.freeze([0, 0, 0]) as Vector3, scale: 1, skin: common.skin, viewModel: true, viewModelProjection: artifact.descriptor }),
        request: Object.freeze(composed),
      })
    },
  })
}
function stable32(value: string) {
  let hash = 0x811c9dc5
  for (const byte of UTF8_ENCODER.encode(value)) {
    hash ^= byte
    hash = Math.imul(hash, 0x01000193)
  }
  return hash >>> 0 || 1
}
function stable64(value: string) {
  let hash = 0xcbf29ce484222325n
  for (const byte of UTF8_ENCODER.encode(value)) {
    hash ^= BigInt(byte)
    hash = BigInt.asUintN(64, hash * 0x100000001b3n)
  }
  return hash || 1n
}

export type ModelPoseRequest = Readonly<{
  itemDefinition?: number
  activityStartTick?: bigint
  prefirePlaybackRate?: number
  allowIdleTransition?: boolean
  identity: number
  classSelection?: boolean
  controlPoint?: number
  modelPanel?: boolean
  entityModelPanel?: boolean
  entityAnimationRevision?: number
  modelPanelReset?: boolean
  cloak?: ActorCloakState
  actorIdentity?: number
  preparation?: boolean
  hudModel?: boolean
  model: string
  itemModel?: string
  worldItem?: boolean
  handsOnlyViewmodel?: boolean
  activity: string
  sampleTick?: bigint
  attachmentsOnly?: boolean
  fireView?: Readonly<{ eyePosition: Vector3; viewOrientation: Quaternion }>
  previousElapsedSeconds: number
  elapsedSeconds: number
  currentTimeSeconds:number;frameTimeSeconds:number;planarSpeed:number;screenAspectRatio:number;worldFarPlane:number
  phase?: 0 | 1 | 2 | 3 | 4 | 5
  reflectedViewmodel?: boolean
  ownerAlive?: boolean
  packedBody?: number
  skin: number
  lod: number
  bodygroups: readonly number[]
  itemBodygroups?: readonly number[]
  equippedItems?: readonly Tf2EquippedItem[]
  lighting?: Readonly<{
    origin: Vector3
    angles: Vector3
    cameraPosition: Vector3
    cameraAngles: Vector3
  }>
}>
export type PosedPrimitive = Readonly<{
  primitive: number
  material: number
  vertexCount: number
  translucent:boolean
  visible:boolean
}>
export type PosedAttachment = Readonly<{ name: string; worldAligned: boolean; matrix: Float32Array }>
export type PosedModel = Readonly<{
  identity: number
  cloak: Readonly<{ localFactor: number; worldFactor: number; rawFactor: number; playerTint: Vector3; local: boolean; player: boolean }> | null
  sampleTick: bigint
  attachmentsOnly: boolean
  attachmentsWorld: boolean
  role: "single" | "hand" | "item" | "wearable"
  wearable: Readonly<{ itemId: number; effect: number; controlPoint: Float32Array; particleBytes: Uint8Array }> | null
  model: string
  activity: string
  sequence: number
  framesPerSecond: number
  weightedFrameCount: number
  cyclesPerSecond: number
  durationSeconds: number
  looping: boolean
  transitioning: boolean
  previousCycle: number
  cycle: number
  boneMatrices: Float32Array
  events: readonly Readonly<{ index: number; cycle: number; event: number; eventType: number; options: Uint8Array; name: string }>[]
  primitives: readonly PosedPrimitive[]
  attachments: readonly PosedAttachment[]
  lighting: ModelLightingInput | null
  eyes: readonly ModelEyeState[]
  flex: readonly Readonly<{ primitive: number; indices: Uint32Array; positions: Float32Array; normals: Float32Array }>[]
  viewmodel:null|Readonly<{transform:Readonly<{origin:Vector3;angles:Vector3}>;projection:Readonly<{unscaledHorizontalFov4By3:number;horizontalFov:number;aspectRatio:number;near:number;far:number}>;depthRange:readonly[number,number];restoredDepthRange:readonly[number,number];passRestored:boolean;depthRestored:boolean;itemTranslucent:boolean;phase:"draw"|"primary-fire"|"reload-start"|"reload-insert-or-loop"|"reload-finish"|"idle";drawDisposition:"draw"|"suppressed-success"|"suppressed";suppression:number|null;reflected:boolean;frontFace:"clockwise"|"counter-clockwise";cullFace:"back";restoredCullMode:"counter-clockwise"|"clockwise";handBodygroups:readonly number[];itemBodygroups:readonly number[];itemBodygroupMutations:readonly Readonly<{event:number;bodygroup:number;value:number;name:string}>[]}>
}>

export function encodeModelPoseBatch(requests: readonly ModelPoseRequest[]): Uint8Array {
  if (requests.length > 128) throw new ProjectilePresentationError("BoundExceeded", "model pose request count")
  let length = 12
  const encodedRequests = requests.map((request) => {
    const model = poseText(request.model)
    const item = poseText(request.itemModel ?? "")
    const activity = poseText(request.activity)
    length += 208 + model.length + item.length + activity.length +
      (request.bodygroups.length + (request.itemBodygroups?.length ?? 0)) * 4
    for (const item of request.equippedItems ?? []) length += 12 + item.attributes.length * 8
    return { request, model, item, activity }
  })
  if (length > 1024 * 1024) throw new ProjectilePresentationError("BoundExceeded", "model pose request bytes")
  const bytes = new Uint8Array(length), view = new DataView(bytes.buffer)
  bytes.set([0x50, 0x4d, 0x52, 0x51])
  view.setUint32(4, 13, true)
  view.setUint32(8, requests.length, true)
  let at = 12
  const text = (encoded: Uint8Array) => {
    view.setUint32(at, encoded.length, true); at += 4; bytes.set(encoded, at); at += encoded.length
  }
  for (const { request, model, item, activity } of encodedRequests) {
    if ((request.classSelection !== undefined && typeof request.classSelection !== "boolean")
      || (request.preparation !== undefined && typeof request.preparation !== "boolean")
      || (request.hudModel !== undefined && typeof request.hudModel !== "boolean")
      || (request.hudModel && (!request.modelPanel || !request.actorIdentity))
      || (request.worldItem !== undefined && typeof request.worldItem !== "boolean")
      || (request.modelPanel !== undefined && typeof request.modelPanel !== "boolean")
      || (request.entityModelPanel !== undefined && typeof request.entityModelPanel !== "boolean")
      || (Boolean(request.entityModelPanel) !== (request.entityAnimationRevision !== undefined))
      || (request.entityModelPanel && (request.modelPanel || request.classSelection || request.itemModel || request.phase !== undefined || request.cloak || request.worldItem || request.handsOnlyViewmodel || request.controlPoint !== undefined || request.actorIdentity !== undefined || request.itemDefinition !== undefined || request.packedBody !== undefined))
      || (request.modelPanelReset !== undefined && typeof request.modelPanelReset !== "boolean")
      || ((request.classSelection || request.modelPanel) && ((request.itemModel !== undefined && (request.classSelection || !request.worldItem)) || request.handsOnlyViewmodel || request.phase !== undefined || request.cloak !== undefined))
      || (request.modelPanelReset && !request.classSelection && !request.modelPanel && !request.entityModelPanel)) {
      throw new ProjectilePresentationError("MalformedFact", "model panel pose request")
    }
    if (!Number.isSafeInteger(request.identity) || request.identity < 1 || !request.model || !request.activity ||
      ![request.previousElapsedSeconds, request.elapsedSeconds].every(Number.isFinite) || request.previousElapsedSeconds < 0 ||
      request.elapsedSeconds < request.previousElapsedSeconds || ![request.skin, request.lod, ...request.bodygroups, ...(request.itemBodygroups ?? [])].every((value) => Number.isSafeInteger(value) && value >= 0) ||
      ((request.itemModel === undefined) !== (request.itemBodygroups === undefined)) || request.itemModel === "") {
      throw new ProjectilePresentationError("MalformedFact", "model pose request")
    }
    view.setUint32(at, request.identity, true); at += 4
    const cloak = request.cloak
    if (cloak && (!Number.isSafeInteger(cloak.identity) || cloak.identity < 1 || cloak.identity > 0xffff_ffff)) throw new ProjectilePresentationError("MalformedFact", "model actor identity")
    if (request.controlPoint !== undefined && (!Number.isSafeInteger(request.controlPoint) || request.controlPoint <= 0 || request.controlPoint > 0xffff_ffff || request.actorIdentity !== undefined || cloak || request.itemModel || request.classSelection || request.modelPanel)) throw new ProjectilePresentationError("MalformedFact", "control point pose identity")
    const actorIdentity = request.entityAnimationRevision ?? request.actorIdentity ?? cloak?.identity ?? 0
    if (!Number.isSafeInteger(actorIdentity) || actorIdentity < 0 || actorIdentity > UINT32_MAX || (cloak && cloak.identity !== actorIdentity)) throw new ProjectilePresentationError("MalformedFact", "model actor identity")
    view.setUint32(at, request.controlPoint ?? actorIdentity, true); at += 4
    for (const value of cloak ? [cloak.localFactor, cloak.worldFactor, cloak.rawFactor, ...cloak.playerTint] : [0, 0, 0, 0, 0, 0]) {
      if (!Number.isFinite(value) || value < 0 || value > 1) throw new ProjectilePresentationError("MalformedFact", "model actor cloak")
      view.setFloat32(at, value, true); at += 4
    }
    const sampleTick = request.sampleTick ?? 0n
    if (typeof sampleTick !== "bigint" || sampleTick < 0n || sampleTick > 0xffff_ffff_ffff_ffffn ||
      typeof (request.attachmentsOnly ?? false) !== "boolean" ||
      (request.attachmentsOnly && request.itemModel === undefined) ||
      (request.attachmentsOnly && request.fireView === undefined) ||
      (request.fireView !== undefined && (request.itemModel === undefined ||
        !finite(request.fireView.eyePosition) || !quaternion(request.fireView.viewOrientation)))) {
      throw new ProjectilePresentationError("MalformedFact", "model pose sample")
    }
    view.setBigUint64(at, sampleTick, true); at += 8
    bytes[at] = request.entityModelPanel ? 8 : request.controlPoint !== undefined ? 7 : request.classSelection ? 3 : request.modelPanel ? request.worldItem ? 6 : 4 : request.worldItem ? 5 : request.handsOnlyViewmodel ? 2 : request.itemModel === undefined ? 0 : 1
    bytes[at + 1] = Number(request.attachmentsOnly ?? false)
    bytes[at + 2] = Number(request.fireView !== undefined)
    bytes[at + 3] = Number(request.modelPanelReset ?? false) | (Number(cloak !== undefined) << 1) | (Number(request.preparation ?? false) << 2) | (Number(request.hudModel ?? false) << 3)
    at += 4
    if (request.itemDefinition !== undefined && (!Number.isSafeInteger(request.itemDefinition)
      || request.itemDefinition < 0 || request.itemDefinition >= 0xffff_ffff)) throw new ProjectilePresentationError("MalformedFact", "item definition")
    const activityStartTick = request.activityStartTick ?? 0xffff_ffff_ffff_ffffn
    if (typeof activityStartTick !== "bigint" || activityStartTick < 0n || activityStartTick > 0xffff_ffff_ffff_ffffn
      || (request.itemDefinition !== undefined && !request.worldItem && !request.modelPanel && !request.classSelection && request.activityStartTick === undefined)) {
      throw new ProjectilePresentationError("MalformedFact", "viewmodel activity clock")
    }
    view.setUint32(at, request.itemDefinition ?? 0xffff_ffff, true); at += 4
    view.setBigUint64(at, activityStartTick, true); at += 8
    const firePosition=request.fireView?.eyePosition,fireOrientation=request.fireView?.viewOrientation
    view.setFloat32(at,firePosition?.[0]??0,true)
    view.setFloat32(at+4,firePosition?.[1]??0,true)
    view.setFloat32(at+8,firePosition?.[2]??0,true)
    view.setFloat32(at+12,fireOrientation?.[0]??0,true)
    view.setFloat32(at+16,fireOrientation?.[1]??0,true)
    view.setFloat32(at+20,fireOrientation?.[2]??0,true)
    view.setFloat32(at+24,fireOrientation?.[3]??0,true)
    at+=28
    text(model); text(item); text(activity)
    view.setFloat32(at, request.previousElapsedSeconds, true); at += 4
    view.setFloat32(at, request.elapsedSeconds, true); at += 4
    view.setFloat32(at,request.currentTimeSeconds,true)
    view.setFloat32(at+4,request.frameTimeSeconds,true)
    view.setFloat32(at+8,request.planarSpeed,true)
    view.setFloat32(at+12,request.screenAspectRatio,true)
    view.setFloat32(at+16,request.worldFarPlane,true)
    at+=20
    view.setUint32(at, request.skin, true); at += 4
    view.setUint32(at, request.lod, true); at += 4
    bytes[at] = request.worldItem || (request.itemModel === undefined && !request.handsOnlyViewmodel) ? 0xff : (request.phase ?? 0xff)
    bytes[at + 1] = Number(request.reflectedViewmodel ?? false)
    bytes[at + 2] = Number(request.ownerAlive ?? true)
    bytes[at + 3] = Number(request.allowIdleTransition ?? false)
    if (((request.itemModel !== undefined && !request.worldItem || request.handsOnlyViewmodel) && (request.phase === undefined || request.phase < 0 || request.phase > 5)) ||
      (request.worldItem && (request.itemModel === undefined || request.phase !== undefined || request.handsOnlyViewmodel || request.fireView !== undefined)) ||
      (request.itemModel === undefined && !request.handsOnlyViewmodel && request.phase !== undefined) ||
      (request.handsOnlyViewmodel && (request.itemModel !== undefined || request.itemBodygroups !== undefined)) ||
      typeof (request.reflectedViewmodel ?? false) !== "boolean" ||
      typeof (request.ownerAlive ?? true) !== "boolean" || typeof (request.allowIdleTransition ?? false) !== "boolean") throw new ProjectilePresentationError("MalformedFact", "viewmodel frame request")
    at += 4
    view.setInt32(at, request.packedBody ?? -0x8000_0000, true);at+=4
    if(request.packedBody!==undefined&&(!Number.isSafeInteger(request.packedBody)||request.packedBody<0||request.itemModel!==undefined))throw new ProjectilePresentationError("MalformedFact","packed model body")
    view.setUint32(at, request.bodygroups.length, true); at += 4
    for (const value of request.bodygroups) { view.setUint32(at, value, true); at += 4 }
    view.setUint32(at, request.itemBodygroups?.length ?? 0, true); at += 4
    for (const value of request.itemBodygroups ?? []) { view.setUint32(at, value, true); at += 4 }
    const equipped = request.equippedItems ?? []
    if (equipped.length > 19 || new Set(equipped.map(item => item.itemId)).size !== equipped.length) throw new ProjectilePresentationError("MalformedFact", "equipped model items")
    view.setUint32(at, equipped.length, true); at += 4
    for (const item of equipped) {
      if (![item.itemId, item.definitionIndex, item.quality, item.style, item.slot].every(Number.isSafeInteger) || item.itemId < 1 || item.itemId > UINT32_MAX || item.definitionIndex < 0 || item.definitionIndex > UINT32_MAX || item.quality < 0 || item.quality > 15 || item.style < 0 || item.style > 255 || item.slot < 0 || item.slot > 18 || item.attributes.length > 64) throw new ProjectilePresentationError("MalformedFact", "equipped model item")
      view.setUint32(at, item.itemId, true); view.setUint32(at + 4, item.definitionIndex, true)
      bytes[at + 8] = item.quality; bytes[at + 9] = item.style; bytes[at + 10] = item.slot; bytes[at + 11] = item.attributes.length; at += 12
      for (const attribute of item.attributes) {
        if (!Number.isSafeInteger(attribute.definition) || attribute.definition < 0 || attribute.definition > UINT32_MAX || !Number.isFinite(attribute.value)) throw new ProjectilePresentationError("MalformedFact", "equipped model attribute")
        view.setUint32(at, attribute.definition, true); view.setFloat32(at + 4, attribute.value, true); at += 8
      }
    }
    const lighting = request.lighting
    if (lighting && (!finite(lighting.origin) || !finite(lighting.angles) || !finite(lighting.cameraPosition) || !finite(lighting.cameraAngles))) {
      throw new ProjectilePresentationError("MalformedFact", "model lighting request")
    }
    bytes[at] = Number(lighting !== undefined)
    at += 4
    view.setFloat32(at,lighting?.origin[0]??0,true)
    view.setFloat32(at+4,lighting?.origin[1]??0,true)
    view.setFloat32(at+8,lighting?.origin[2]??0,true)
    view.setFloat32(at+12,lighting?.angles[0]??0,true)
    view.setFloat32(at+16,lighting?.angles[1]??0,true)
    view.setFloat32(at+20,lighting?.angles[2]??0,true)
    view.setFloat32(at+24,lighting?.cameraPosition[0]??0,true)
    view.setFloat32(at+28,lighting?.cameraPosition[1]??0,true)
    view.setFloat32(at+32,lighting?.cameraPosition[2]??0,true)
    view.setFloat32(at+36,lighting?.cameraAngles[0]??0,true)
    view.setFloat32(at+40,lighting?.cameraAngles[1]??0,true)
    view.setFloat32(at+44,lighting?.cameraAngles[2]??0,true)
    at+=48
    const rate = request.prefirePlaybackRate
    if (rate !== undefined && (!Number.isFinite(Math.fround(rate)) || Math.fround(rate) <= 0)) {
      throw new ProjectilePresentationError("MalformedFact", "viewmodel prefire playback rate")
    }
    view.setFloat32(at, rate ?? 0, true); at += 4
  }
  return bytes
}

export function decodeModelPoseOutput(bytes: Uint8Array): readonly PosedModel[] {
  if (bytes.byteLength < 12 || bytes.byteLength > 64 * 1024 * 1024) throw new ProjectilePresentationError("BoundExceeded", "model pose output bytes")
  if (bytes.byteOffset % Float32Array.BYTES_PER_ELEMENT !== 0) throw new ProjectilePresentationError("MalformedFact", "model pose output alignment")
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength)
  if (view.getUint32(0, false) !== 0x504d504f || view.getUint32(4, true) !== 11) throw new ProjectilePresentationError("MalformedFact", "model pose output identity")
  let at = 12
  const ensure = (length: number) => { if (at + length > bytes.length) throw new ProjectilePresentationError("MalformedFact", "model pose output truncation") }
  const u8 = () => { ensure(1); return bytes[at++]! }, u32 = () => { ensure(4); const value = view.getUint32(at, true); at += 4; return value },
    i32 = () => { ensure(4); const value = view.getInt32(at, true); at += 4; return value },
    f32 = () => { ensure(4); const value = view.getFloat32(at, true); at += 4; if (!Number.isFinite(value)) throw new ProjectilePresentationError("MalformedFact", "model pose scalar"); return value },
    text = () => { const length = u32(); ensure(length); const range = bytes.subarray(at, at + length); const value = UTF8_DECODER.decode(range.buffer instanceof SharedArrayBuffer ? range.slice() : range); at += length; return value }
  const output: PosedModel[] = []
  for (let count = view.getUint32(8, true); count > 0; count--) {
    const identity = u32()
    const hasCloak = u8(), localCloak = u8(), playerCloak = u8()
    if (hasCloak > 1 || localCloak > 1 || playerCloak > 1 || u8() || (!hasCloak && (localCloak || playerCloak))) throw new ProjectilePresentationError("MalformedFact", "model cloak flags")
    const localFactor = f32(), worldFactor = f32(), rawFactor = f32(), playerTint = vector([f32(), f32(), f32()])
    if ([localFactor, worldFactor, rawFactor, ...playerTint].some(value => value < 0 || value > 1 || (!hasCloak && value !== 0))) throw new ProjectilePresentationError("MalformedFact", "model cloak state")
    const cloak = hasCloak ? Object.freeze({ localFactor, worldFactor, rawFactor, playerTint, local: localCloak === 1, player: playerCloak === 1 }) : null
    ensure(8)
    const sampleTick = view.getBigUint64(at, true); at += 8
    const roleCode = u8(), attachmentMode = u8(), attachmentsWorld = u8()
    if (roleCode > 3 || attachmentMode > 1 || attachmentsWorld > 1 ||
      (attachmentMode === 1 && (roleCode !== 2 || attachmentsWorld !== 1)) || u8()) {
      throw new ProjectilePresentationError("MalformedFact", "model pose role")
    }
    let wearable: PosedModel["wearable"] = null
    if (roleCode === 3) {
      const itemId = u32(), effect = u32(), controlPoint = Float32Array.from({ length: 12 }, f32)
      if (itemId === 0 || (effect !== 0 && effect !== 13)) throw new ProjectilePresentationError("MalformedFact", "wearable effect")
      const particleLength = u32(); ensure(particleLength)
      const particleBytes = bytes.slice(at, at + particleLength); at += particleLength
      wearable = Object.freeze({ itemId, effect, controlPoint, particleBytes })
    }
    const model = text(), activity = text(), sequence = u32(), framesPerSecond = f32(), weightedFrameCount = f32(),
      cyclesPerSecond = f32(), durationSeconds = f32(), looping = u8(), transitioning = u8()
    if (looping > 1 || transitioning > 1 || u8() || u8()) throw new ProjectilePresentationError("MalformedFact", "model pose timing")
    const previousCycle = f32(), cycle = f32()
    const present=u8();if(present>1||u8()||u8()||u8())throw new ProjectilePresentationError("MalformedFact","viewmodel state");const values=present===1?new Array<number>(15):null;for(let index=0;index<15;index+=1){const value=f32();if(values)values[index]=value}const passRestored=u8(),depthRestored=u8(),itemTranslucent=u8();if(u8())throw new ProjectilePresentationError("MalformedFact","viewmodel flags")
    const phase=u8(),drawDisposition=u8(),suppression=u8(),reflected=u8(),frontFace=u8(),cullFace=u8(),restoredCull=u8(),reserved=u8()
    if(phase>5||drawDisposition>2||suppression>12||reflected>1||frontFace>1||cullFace!==0||restoredCull>1||reserved||
      (drawDisposition===0)!==(suppression===0))throw new ProjectilePresentationError("MalformedFact","viewmodel frame state")
    const valuesArray=(limit:number)=>{const count=u32();if(count>limit)throw new ProjectilePresentationError("BoundExceeded","viewmodel frame values");return count===0?EMPTY_POSE_VALUES:Object.freeze(Array.from({length:count},u32))}
    const handBodygroups=valuesArray(64),itemBodygroups=valuesArray(64),mutationCount=u32();if(mutationCount>64)throw new ProjectilePresentationError("BoundExceeded","viewmodel bodygroup mutations")
    const itemBodygroupMutations=mutationCount===0?EMPTY_POSE_VALUES:Object.freeze(Array.from({length:mutationCount},()=>Object.freeze({event:u32(),bodygroup:u32(),value:i32(),name:text()})))
    if(present===0&&(phase||drawDisposition||suppression||reflected||frontFace||cullFace||restoredCull||handBodygroups.length||itemBodygroups.length||itemBodygroupMutations.length))throw new ProjectilePresentationError("MalformedFact","absent viewmodel frame state")
    const viewmodel=values===null?null:Object.freeze({transform:Object.freeze({origin:vector(values.slice(0,3)),angles:vector(values.slice(3,6))}),projection:Object.freeze({unscaledHorizontalFov4By3:values[6]!,horizontalFov:values[7]!,aspectRatio:values[8]!,near:values[9]!,far:values[10]!}),depthRange:Object.freeze(values.slice(11,13)) as readonly[number,number],restoredDepthRange:Object.freeze(values.slice(13,15)) as readonly[number,number],passRestored:passRestored===1,depthRestored:depthRestored===1,itemTranslucent:itemTranslucent===1,phase:(["draw","primary-fire","reload-start","reload-insert-or-loop","reload-finish","idle"] as const)[phase]!,drawDisposition:(["draw","suppressed-success","suppressed"] as const)[drawDisposition]!,suppression:suppression===0?null:suppression,reflected:reflected===1,frontFace:frontFace===0?"clockwise" as const:"counter-clockwise" as const,cullFace:"back" as const,restoredCullMode:restoredCull===0?"counter-clockwise" as const:"clockwise" as const,handBodygroups,itemBodygroups,itemBodygroupMutations})
    const eventCount = u32()
    const events = eventCount === 0 ? EMPTY_POSE_VALUES : Object.freeze(Array.from({ length: eventCount }, () => {
      const index = u32(), eventCycle = f32(), event = i32(), eventType = i32(); ensure(64)
      const options = bytes.slice(at, at + 64); at += 64
      return Object.freeze({ index, cycle: eventCycle, event, eventType, options, name: text() })
    }))
    const boneCount = u32()
    if (boneCount > 256) throw new ProjectilePresentationError("BoundExceeded", "model bone matrix count")
    ensure(boneCount * 48)
    const boneMatrices = new Float32Array(boneCount * 12)
    for (let index = 0; index < boneMatrices.length; index += 1) boneMatrices[index] = f32()
    const primitives = Object.freeze(Array.from({ length: u32() }, () => {
      const primitive = u32(), material = u32(), vertices = u32(), translucent = u8(), visible = u8()
      if (translucent > 1 || visible > 1 || u8() || u8()) {
        throw new ProjectilePresentationError("MalformedFact", "primitive opacity")
      }
      return Object.freeze({ primitive, material, vertexCount: vertices, translucent: translucent === 1, visible: visible === 1 })
    }))
    const attachmentCount = u32()
    const attachments = attachmentCount === 0 ? EMPTY_POSE_VALUES : Object.freeze(Array.from({ length: attachmentCount }, () => {
      const name = text(), worldAligned = u8(); if (worldAligned > 1 || u8() || u8() || u8()) throw new ProjectilePresentationError("MalformedFact", "model attachment")
      const matrix = new Float32Array(12); for (let index = 0; index < 12; index++) matrix[index] = f32()
      return Object.freeze({ name, worldAligned: worldAligned === 1, matrix })
    }))
    const hasLighting = u8(), localCount = u8(), ambientLight = u8(), lightingReserved = u8()
    if (hasLighting > 1 || localCount > 4 || ambientLight > 1 || lightingReserved ||
      (hasLighting === 0 && (localCount !== 0 || ambientLight !== 0))) {
      throw new ProjectilePresentationError("MalformedFact", "model world lighting")
    }
    let lighting: ModelLightingInput | null = null
    if (hasLighting) {
      const lightingOrigin = vector([f32(), f32(), f32()])
      const cameraPosition = vector([f32(), f32(), f32()])
      const ambientCube = Object.freeze(Array.from({ length: 6 }, () => vector([f32(), f32(), f32()]))) as ModelLightingInput["ambientCube"]
      const localLights = localCount === 0 ? EMPTY_POSE_VALUES : Object.freeze(Array.from({ length: localCount }, (): ModelLocalLight => {
        const kind = u8()
        if (kind > 2 || u8() || u8() || u8()) throw new ProjectilePresentationError("MalformedFact", "model local light")
        return Object.freeze({
          kind: (["point", "directional", "spot"] as const)[kind]!,
          color: vector([f32(), f32(), f32()]),
          position: vector([f32(), f32(), f32()]),
          direction: vector([f32(), f32(), f32()]),
          range: f32(),
          falloff: f32(),
          attenuation: vector([f32(), f32(), f32()]),
          theta: f32(),
          phi: f32(),
        })
      }))
      const environment = text()
      lighting = Object.freeze({
        lightingOrigin,
        ambientCube,
        localLights,
        cameraPosition,
        localEnvironment: environment || null,
        ambientLight: ambientLight === 1,
        staticLightVertex: false,
        staticLightTexel: false,
      })
    }
    const eyeCount = u32()
    if (eyeCount > primitives.length) throw new ProjectilePresentationError("BoundExceeded", "model eye-state count")
    const eyes = eyeCount === 0 ? EMPTY_POSE_VALUES : Object.freeze(Array.from({ length: eyeCount }, (): ModelEyeState => {
      const primitive = u32(), mesh = u32(), eyeball = u32(), texture = u32()
      const worldOrigin = vector([f32(), f32(), f32()])
      const authoredUp = vector([f32(), f32(), f32()])
      const row = () => Object.freeze([f32(), f32(), f32(), f32()]) as readonly [number, number, number, number]
      return Object.freeze({ primitive, mesh, eyeball, texture, worldOrigin, authoredUp,
        irisU: row(), irisV: row(), glintU: row(), glintV: row() })
    }))
    const flexCount = u32()
    if (flexCount > primitives.length) throw new ProjectilePresentationError("BoundExceeded", "model flex primitive count")
    const flex = flexCount === 0 ? EMPTY_POSE_VALUES : Object.freeze(Array.from({ length: flexCount }, () => {
      const primitive = u32(), count = u32()
      const authored = primitives.find((value) => value.primitive === primitive)
      if (!authored || count > authored.vertexCount) throw new ProjectilePresentationError("MalformedFact", "model flex vertex count")
      const indices = new Uint32Array(count), positions = new Float32Array(count * 3), normals = new Float32Array(count * 3)
      for (let i = 0; i < count; i += 1) {
        const index = u32()
        if (index >= authored.vertexCount || i > 0 && index <= indices[i - 1]!) throw new ProjectilePresentationError("MalformedFact", "model flex vertex index")
        indices[i] = index
        for (let axis = 0; axis < 3; axis += 1) positions[i * 3 + axis] = f32()
        for (let axis = 0; axis < 3; axis += 1) normals[i * 3 + axis] = f32()
      }
      return Object.freeze({ primitive, indices, positions, normals })
    }))
    if (attachmentMode === 1 && (primitives.length !== 0 || eyes.length !== 0 || flex.length !== 0)) throw new ProjectilePresentationError("MalformedFact", "attachment-only pose contains geometry")
    output.push(Object.freeze({ identity, cloak, sampleTick, attachmentsOnly: attachmentMode === 1, attachmentsWorld: attachmentsWorld === 1, role: (["single", "hand", "item", "wearable"] as const)[roleCode]!, wearable, model, activity, sequence, framesPerSecond, weightedFrameCount, cyclesPerSecond, durationSeconds, looping: looping === 1, transitioning: transitioning === 1, previousCycle, cycle, boneMatrices, events, primitives, attachments, lighting, eyes, flex, viewmodel }))
  }
  if (at !== bytes.length) throw new ProjectilePresentationError("MalformedFact", "model pose output trailing bytes")
  return Object.freeze(output)
}
export type ParticleVisibilityFrame = Readonly<{
  yawDegrees: number; pitchDegrees: number; verticalFovDegrees: number; width: number; height: number
  samples: readonly Readonly<{ identity: bigint; visiblePixels: number; possiblePixels: number; clipFraction: number }>[]
}>

export function createParticleBatchEncoder() {
  let previousTick = 0n
  let previousTime = 0
  return Object.freeze({
    encode(tick: bigint, camera: Vector3, requests: readonly ProjectileParticleRequest[], visibility?: ParticleVisibilityFrame) {
      if (
        typeof tick !== "bigint"
        || tick < previousTick
        || tick > BigInt(Number.MAX_SAFE_INTEGER)
        || !Array.isArray(requests)
        || requests.length > 4_096
        || !finite(camera)
        || camera.some(value => !Number.isFinite(Math.fround(value)))
      ) {
        throw new ProjectilePresentationError("TimeReversed", "particle transaction range or input is invalid")
      }
      const to = Math.fround(Number(tick) * 0.015)
      if (!Number.isFinite(to) || to < previousTime) {
        throw new ProjectilePresentationError("TimeReversed", "particle transaction range or input is invalid")
      }

      if (visibility && (![visibility.yawDegrees, visibility.pitchDegrees, visibility.verticalFovDegrees, visibility.width, visibility.height].every(Number.isFinite)
        || visibility.verticalFovDegrees <= 0 || visibility.verticalFovDegrees >= 180 || visibility.width <= 0 || visibility.height <= 0
        || visibility.samples.length > 4096 || visibility.samples.some((sample, index) => typeof sample.identity !== "bigint" || sample.identity < 0n || sample.identity > 0xffff_ffff_ffff_ffffn
          || index > 0 && sample.identity <= visibility.samples[index - 1]!.identity
          || ![sample.visiblePixels, sample.possiblePixels].every(value => Number.isInteger(value) && value >= -1 && value <= 0x7fff_ffff)
          || !Number.isFinite(sample.clipFraction) || sample.clipFraction < 0 || sample.clipFraction > 1))) {
        throw new ProjectilePresentationError("MalformedFact", "particle visibility feedback is invalid")
      }
      let length = 40 + (visibility ? 20 + visibility.samples.length * 20 : 0)
      let previousRequestTick = previousTick
      const systems = new Map<string, Uint8Array>()
      const identities = new Set<bigint>()
      const requestIdentities: bigint[] = []
      for (const request of requests) {
        if (
          typeof request.tick !== "bigint"
          || request.tick < previousRequestTick
          || request.tick > tick
        ) {
          throw new ProjectilePresentationError("TimeReversed", "particle request time is outside source order")
        }
        if (
          !particleIdentity(request.identity)
          || !particleIdentity(request.effectIdentity)
          || !particleIdentity(request.eventIdentity)
          || !uint32(request.projectileIdentity)
        ) {
          throw new ProjectilePresentationError("MalformedFact", "particle request identity is invalid")
        }
        const identity = stable64(request.identity)
        if (identities.has(identity)) {
          throw new ProjectilePresentationError("MalformedFact", "particle request identity is duplicated")
        }
        identities.add(identity)
        requestIdentities.push(identity)
        previousRequestTick = request.tick
        length += 20

        if (request.kind === "start") {
          if (
            !uint32(request.ownerIdentity)
            || !uint32(request.launcherIdentity)
            || (request.team !== "red" && request.team !== "blue")
            || typeof request.system !== "string"
            || request.system.length === 0
            || !Array.isArray(request.controlPoints)
            || request.controlPoints.length < 1
            || request.controlPoints.length > 2
            || request.controlPoints.some((control, index) => !particleControlPoint(control)
              || control.index !== index || (index===0&&control.ownerIdentity !== request.ownerIdentity))
            || (request.attachment !== null && (
              !uint32(request.attachment.entityIdentity)
              || !["backblast", "muzzle", "trail"].includes(request.attachment.name)
            ))
          ) {
            throw new ProjectilePresentationError("MalformedFact", "particle start request is invalid")
          }
          if (request.system.length > 1_024) {
            throw new ProjectilePresentationError("BoundExceeded", "particle definition identity exceeds its limit")
          }
          let encoded = systems.get(request.system)
          if (!encoded) {
            encoded = UTF8_ENCODER.encode(request.system)
            if (encoded.byteLength > 1_024) {
              throw new ProjectilePresentationError("BoundExceeded", "particle definition identity exceeds its limit")
            }
            systems.set(request.system, encoded)
          }
          length += 16 + encoded.byteLength + request.controlPoints.length * 32
        } else if (request.kind === "set-control-point") {
          if (!particleControlPoint(request.controlPoint)) {
            throw new ProjectilePresentationError("MalformedFact", "particle control-point request is invalid")
          }
          length += 32
        } else if (request.kind === "set-flame-control-point") {
          const control=request.controlPoint
          if(!Number.isInteger(control.index)||control.index<1||control.index>30||!finite(control.position)||!quaternion(control.orientation)||!finite(control.velocity)||![control.radius,control.density,control.duration].every(value=>Number.isFinite(value)&&value>=0)||!uint32(control.ownerIdentity)){
            throw new ProjectilePresentationError("MalformedFact", "flame control-point request is invalid")
          }
          length += 60
        } else if (request.kind === "stop") {
          if (typeof request.immediate !== "boolean") {
            throw new ProjectilePresentationError("MalformedFact", "particle stop mode is invalid")
          }
        } else {
          throw new ProjectilePresentationError("MalformedFact", "particle request command is invalid")
        }
        if (length > 4 * 1024 * 1024) {
          throw new ProjectilePresentationError("BoundExceeded", "particle transaction bytes")
        }
      }

      const bytes = new Uint8Array(length)
      const view = new DataView(bytes.buffer)
      bytes.set([0x50, 0x50, 0x54, 0x58])
      view.setUint32(4, 5, true)
      view.setFloat32(8, previousTime, true)
      view.setFloat32(12, to, true)
      camera.forEach((value, index) => view.setFloat32(16 + index * 4, value, true))
      view.setUint32(28, requests.length, true)
      let at = 32
      for (let index = 0; index < requests.length; index += 1) {
        const request = requests[index]!
        bytes[at] = request.kind === "start"?1:request.kind === "set-control-point" ? 2 : request.kind === "set-flame-control-point" ? 4 : 3
        bytes[at + 1] = request.kind === "stop" && request.immediate ? 1 : 0
        bytes[at + 2] = request.kind === "start" ? request.controlPoints.length
          : request.kind === "set-control-point" ? request.controlPoint.index : 0
        view.setBigUint64(at + 4, requestIdentities[index]!, true)
        view.setFloat32(at + 12, Math.fround(Number(request.tick) * 0.015), true)
        view.setUint32(at + 16, stable32(request.effectIdentity), true)
        at += 20
        if (request.kind === "start") {
          view.setBigUint64(at, stable64(request.eventIdentity), true)
          view.setUint32(at + 8, request.ownerIdentity, true)
          const system = systems.get(request.system)!
          view.setUint32(at + 12, system.byteLength, true)
          bytes.set(system, at + 16)
          at += 16 + system.byteLength
          for (const control of request.controlPoints) {
            control.position.forEach((value, index) => view.setFloat32(at + index * 4, value, true))
            control.orientation.forEach((value, index) => view.setFloat32(at + 12 + index * 4, value, true))
            view.setUint32(at + 28, control.ownerIdentity, true)
            at += 32
          }
        } else if (request.kind === "set-control-point") {
          const control = request.controlPoint
          control.position.forEach((value, index) => view.setFloat32(at + index * 4, value, true))
          control.orientation.forEach((value, index) => view.setFloat32(at + 12 + index * 4, value, true))
          view.setUint32(at + 28, control.ownerIdentity, true)
          at += 32
        } else if (request.kind === "set-flame-control-point") {
          const control=request.controlPoint
          bytes[at]=control.index
          control.position.forEach((value,index)=>view.setFloat32(at+4+index*4,value,true))
          control.orientation.forEach((value,index)=>view.setFloat32(at+16+index*4,value,true))
          control.velocity.forEach((value,index)=>view.setFloat32(at+32+index*4,value,true))
          view.setFloat32(at+44,control.radius,true)
          view.setFloat32(at+48,control.density,true)
          view.setFloat32(at+52,control.duration,true)
          view.setUint32(at+56,control.ownerIdentity,true)
          at+=60
        }
      }
      view.setUint32(at, visibility ? 1 : 0, true); at += 4
      if (visibility) for (const value of [visibility.yawDegrees, visibility.pitchDegrees, visibility.verticalFovDegrees, visibility.width, visibility.height]) { view.setFloat32(at, value, true); at += 4 }
      view.setUint32(at, visibility?.samples.length ?? 0, true); at += 4
      for (const sample of visibility?.samples ?? []) {
        view.setBigUint64(at, sample.identity, true); view.setInt32(at + 8, sample.visiblePixels, true); view.setInt32(at + 12, sample.possiblePixels, true); view.setFloat32(at + 16, sample.clipFraction, true); at += 20
      }
      previousTick = tick
      previousTime = to
      return bytes
    },
  })
}

/** Render-only operation on the existing particle channel. PCF simulation and
 * its event clock are not advanced by an accepted legacy client frame. */
export function encodeLegacyParticleFrame(timeSeconds: number,
  frame: Pick<import("./client-render-frame").ClientRenderFrame, "clientFrame" | "acceptedClientFrame" | "clientFrameSeconds">,
  camera: Readonly<{ position: Vector3; yawDegrees: number; pitchDegrees: number; verticalFovDegrees: number; aspectRatio: number }>,
  visualPayload: Uint8Array = new Uint8Array(0)): Uint8Array {
  if (![timeSeconds, frame.clientFrameSeconds, ...camera.position, camera.yawDegrees, camera.pitchDegrees, camera.verticalFovDegrees, camera.aspectRatio].every(value => Number.isFinite(Math.fround(value))) || timeSeconds < 0 || frame.clientFrameSeconds < 0 || !uint32(frame.clientFrame) || (frame.acceptedClientFrame !== 0 && !uint32(frame.acceptedClientFrame)) || frame.acceptedClientFrame >= frame.clientFrame || camera.verticalFovDegrees <= 0 || camera.verticalFovDegrees >= 180 || camera.aspectRatio <= 0) {
    throw new ProjectilePresentationError("MalformedFact", "legacy particle frame is invalid")
  }
  if (!(visualPayload instanceof Uint8Array) || visualPayload.byteLength > 4 * 1024 * 1024 - 64) throw new ProjectilePresentationError("BoundExceeded", "legacy visual frame payload is invalid")
  const bytes = new Uint8Array(64 + visualPayload.byteLength), view = new DataView(bytes.buffer)
  bytes.set([0x50, 0x50, 0x54, 0x58]); view.setUint32(4, 5, true)
  view.setFloat32(8, timeSeconds, true); view.setFloat32(12, timeSeconds, true)
  camera.position.forEach((value, index) => view.setFloat32(16 + index * 4, value, true))
  view.setUint32(28, 0x8000_0000, true); view.setFloat32(32, frame.clientFrameSeconds, true)
  view.setUint32(36, frame.acceptedClientFrame, true); view.setUint32(40, frame.clientFrame, true)
  ;[camera.yawDegrees, camera.pitchDegrees, camera.verticalFovDegrees, camera.aspectRatio].forEach((value, index) => view.setFloat32(44 + index * 4, value, true))
  view.setUint32(60, visualPayload.byteLength, true); bytes.set(visualPayload, 64)
  return bytes
}

function particleIdentity(value: string): boolean {
  return typeof value === "string" && value.length > 0
}

function particleControlPoint(value: ParticleControlPoint | undefined): value is ParticleControlPoint {
  return value !== undefined
    && Number.isInteger(value.index) && value.index >= 0 && value.index <= 30
    && finite(value.position)
    && value.position.every(component => Number.isFinite(Math.fround(component)))
    && quaternion(value.orientation)
    && value.orientation.every(component => Number.isFinite(Math.fround(component)))
    && uint32(value.ownerIdentity)
}

export function tf2Hud(snapshot: Snapshot): Tf2Hud {
  return Object.freeze({
    health: snapshot.health,
    maxHealth: snapshot.maximumHealth,
    className: tf2ClassPresentation(snapshot.class).displayName,
    weaponName: snapshot.weapon === null ? null
      : snapshot.weapon === 40 ? "Shotgun"
        : snapshot.weapon === 41 ? "Pistol"
          : snapshot.weapon === 42 ? "Wrench"
            : snapshot.weapon === 43 ? "Construction PDA" : snapshot.weapon === 44 ? "Destruction PDA" : snapshot.weapon === 45 ? "Toolbox"
            : snapshot.weapon === 50 ? "Revolver" : snapshot.weapon === 51 ? "Knife" : snapshot.weapon === 52 ? "Sapper" : snapshot.weapon === 53 ? "Disguise Kit" : snapshot.weapon === 54 ? "Invisibility Watch"
            : (["", "Rocket Launcher", "Original", "Stickybomb Launcher", "Scattergun", "Pistol", "Bat", "Shotgun", "Shovel", "Minigun", "Shotgun", "Fists", "Sniper Rifle", "SMG", "Kukri", "Flamethrower", "Fire Axe", "Bottle", "Grenade Launcher", "Syringe Gun", "Medi Gun", "Bonesaw"] as const)[snapshot.weapon],

    speed: Math.hypot(...snapshot.velocity),
    projectileCount: snapshot.projectiles.length,
  })
}

export function tf2Camera(snapshot: Snapshot, yawDegrees: number, pitchDegrees: number): Camera {
  return Object.freeze({
    position: Object.freeze([
      snapshot.position[0] + snapshot.movement.viewOffset[0],
      snapshot.position[1] + snapshot.movement.viewOffset[1],
      snapshot.position[2] + snapshot.movement.viewOffset[2],
    ]) as readonly [number, number, number],
    yawDegrees: Math.fround(Math.fround(yawDegrees) + snapshot.viewAngleOffset[1]),
    pitchDegrees: Math.fround(Math.fround(pitchDegrees) + snapshot.viewAngleOffset[0]),
    verticalFovDegrees: sourceHorizontal4By3FovToVertical(snapshot.class === 2 && snapshot.weapon === 12 && (snapshot.conditions[0] & 2) !== 0 ? 20 : TF2_DEFAULT_HORIZONTAL_FOV_4_BY_3),
    near: SOURCE_WORLD_NEAR,
    far: Math.fround(SOURCE_MAP_EXTENT * SOURCE_MAP_EXTENT_DIAGONAL),
  })
}

export function particleEffects(items: readonly ParticleRenderItem[]): readonly Effect[] {
  return Object.freeze(
    items.map((item) =>
      Object.freeze({
        identity: item.identity,
        position: item.position,
        radius: item.radius,
        color: item.color,
        opacity: item.opacity,
      }),
    ),
  )
}

export type ProjectileKind = "rocket" | "sticky" | "syringe" | "flare" | "grenade"
export function projectileModelPath(kind: ProjectileKind | Snapshot["projectiles"][number]["kind"], miniRocket: boolean): ProjectileModelRequest["model"] {
  if (kind === "rocket" || kind === 1) return miniRocket
    ? "models/weapons/w_models/w_rocket_airstrike/w_rocket_airstrike.mdl" : "models/weapons/w_models/w_rocket.mdl"
  if (kind === "sticky" || kind === 2) return "models/weapons/w_models/w_stickybomb.mdl"
  if (kind === "grenade" || kind === 5) return "models/weapons/w_models/w_grenade_grenadelauncher.mdl"
  if (kind === "syringe" || kind === 3) return "models/weapons/w_models/w_syringe_proj.mdl"
  return "models/weapons/w_models/w_flaregun_shell.mdl"
}
export type ProjectileTeam = "red" | "blue"
export type ProjectileState = "flying" | "stuck-unarmed" | "stuck-armed"
export type ProjectileEventKind =
  | "fire"
  | "impact"
  | "bounce"
  | "stick"
  | "arm"
  | "fizzle"
  | "explode"
  | "destroy"
  | "deflect"
export type Vector3 = readonly [number, number, number]
export type Quaternion = readonly [number, number, number, number]

export type ProjectileFact = Readonly<{
  airBurst: boolean
  underwaterExplosion: boolean
  weapon: number
  critical: boolean
  trail: number
  miniRocket: boolean
  practiceExplosion: boolean
  selfBlastOnly: boolean
  modelVisible: boolean
  identity: number
  kind: ProjectileKind
  team: ProjectileTeam
  ownerIdentity: number
  launcherIdentity: number
  state: ProjectileState
  position: Vector3
  velocity: Vector3
  orientation: Quaternion
  angularVelocity: Vector3
  contactNormal: Vector3 | null
  ageSeconds: number
}>

export type ProjectileEvent = Readonly<{
  airBurst: boolean
  underwaterExplosion: boolean
  weapon: number
  critical: boolean
  trail: number
  miniRocket: boolean
  practiceExplosion: boolean
  selfBlastOnly: boolean
  kind: ProjectileEventKind
  projectileKind: ProjectileKind
  projectileIdentity: number
  ownerIdentity: number
  launcherIdentity: number
  team: ProjectileTeam
  tick: bigint
  sourceOrdinal: number
  sourceEventOrdinal: number
  position: Vector3
  orientation: Quaternion
  contactNormal: Vector3 | null
}>

export type ProjectileTick = Readonly<{
  tick: bigint
  projectiles: readonly ProjectileFact[]
  events: readonly ProjectileEvent[]
}>

export type ProjectileFrame = Readonly<{
  ticks: readonly ProjectileTick[]
}>

export type ProjectileModelRequest = Readonly<{
  identity: number
  projectileIdentity: number
  model: "models/weapons/w_models/w_rocket.mdl" | "models/weapons/w_models/w_rocket_airstrike/w_rocket_airstrike.mdl" | "models/weapons/w_models/w_flaregun_shell.mdl" | "models/weapons/w_models/w_stickybomb.mdl" | "models/weapons/w_models/w_syringe_proj.mdl" | "models/weapons/w_models/w_grenade_grenadelauncher.mdl"
  skin: 0 | 1
  materialVariant: ProjectileTeam
  position: Vector3
  orientation: Quaternion
  angularVelocity: Vector3
  state: ProjectileState
}>

export type ParticleAttachment = Readonly<{
  entityIdentity: number
  name: "backblast" | "muzzle" | "trail"
}>
export type AttachmentTransform = Readonly<{ position: Vector3; orientation: Quaternion }>

export type ParticleControlPoint = Readonly<{
  index: number
  position: Vector3
  orientation: Quaternion
  ownerIdentity: number
}>

export function weaponParticleColorRequests(start: Extract<ProjectileParticleRequest, { kind: "start" }>): readonly ProjectileParticleRequest[] {
  const colors: readonly Vector3[] = start.team === "red" ? [[0.72, 0.22, 0.23], [0.5, 0.18, 0.125]]
    : [[0.345, 0.52, 0.635], [0.145, 0.427, 0.55]]
  return Object.freeze(colors.map((position, index) => Object.freeze({ kind: "set-control-point" as const,
    identity: `${start.identity}:color:${9 + index}`, effectIdentity: start.effectIdentity, eventIdentity: start.eventIdentity,
    tick: start.tick, projectileIdentity: start.projectileIdentity,
    controlPoint: Object.freeze({ index: 9 + index, position: Object.freeze([...position]) as Vector3,
      orientation: Object.freeze([0, 0, 0, 1]) as Quaternion, ownerIdentity: UINT32_MAX }),
  })))
}
export type FlameControlPoint = Readonly<{
  index: number
  position: Vector3
  orientation: Quaternion
  velocity: Vector3
  radius: number
  density: number
  duration: number
  ownerIdentity: number
}>

export type ProjectileParticleRequest =
  | Readonly<{
      kind: "start"
      identity: string
      effectIdentity: string
      eventIdentity: string
      tick: bigint
      projectileIdentity: number
      ownerIdentity: number
      launcherIdentity: number
      team: ProjectileTeam
      system: string
      attachment: ParticleAttachment | null
      controlPoints: readonly ParticleControlPoint[]
    }>
  | Readonly<{
      kind: "set-control-point"
      identity: string
      effectIdentity: string
      eventIdentity: string
      tick: bigint
      projectileIdentity: number
      controlPoint: ParticleControlPoint
    }>
  | Readonly<{
      kind: "set-flame-control-point"
      identity: string
      effectIdentity: string
      eventIdentity: string
      tick: bigint
      projectileIdentity: number
      controlPoint: FlameControlPoint
    }>
  | Readonly<{
      kind: "stop"
      identity: string
      effectIdentity: string
      eventIdentity: string
      tick: bigint
      projectileIdentity: number
      immediate: boolean
    }>

export function hitscanMuzzleParticles(
  snapshot: Snapshot,
  catalog: Pick<ProjectileResourceCatalog, "systems" | "attachmentTransforms">,
): readonly ProjectileParticleRequest[] {
  const requests: ProjectileParticleRequest[] = []
  for (const event of snapshot.events) {
    if (event.kind !== 12 || (event.auxiliary !== undefined && event.auxiliary !== 0 && event.auxiliary !== 1)
      || (event.detail !== 4 && event.detail !== 5 && event.detail !== 7 && event.detail !== 10 && event.detail !== 50 && event.detail !== 40 && event.detail !== 41)) continue
    const system = event.detail === 4 ? "muzzle_scattergun" : event.detail === 5 || event.detail === 41 ? "muzzle_pistol" : event.detail === 50 ? "muzzle_revolver" : "muzzle_shotgun"
    if (!catalog.systems.has(system)) throw new ProjectilePresentationError("MissingSystem", system)
    const transform = catalog.attachmentTransforms?.get(event.detail)?.get("muzzle")
    if (!transform) throw new ProjectilePresentationError("MissingAttachment", `${event.detail}:muzzle`)
    const eventIdentity = `hitscan:${snapshot.tick}:${event.detail}:${requests.length}`
    requests.push(Object.freeze({
      kind: "start",
      identity: eventIdentity,
      effectIdentity: eventIdentity,
      eventIdentity,
      tick: snapshot.tick,
      projectileIdentity: 0x7fff_0000 + event.detail,
      ownerIdentity: 1,
      launcherIdentity: event.detail,
      team: snapshot.team === 2 ? "red" : "blue",
      system,
      attachment: Object.freeze({ entityIdentity: event.detail, name: "muzzle" }),
      controlPoints: Object.freeze([Object.freeze({
        index: 0,
        position: transform.position,
        orientation: transform.orientation,
        ownerIdentity: 1,
      })]),
    }))
  }
  return Object.freeze(requests)
}

export type CombatPresentationState = Readonly<{ tracerCount: number }>

export function combatImpactParticles(
  snapshot: Snapshot,
  state: CombatPresentationState,
  catalog: Pick<ProjectileResourceCatalog, "systems" | "attachmentTransforms"> & Readonly<{
    playerAttachmentTransforms?: ReadonlyMap<number, ReadonlyMap<string, AttachmentTransform>>
    playerActors?: ReadonlyMap<number, Snapshot["bots"][number]>
  }>,
): Readonly<{ particles: readonly ProjectileParticleRequest[]; state: CombatPresentationState }> {
  const particles: ProjectileParticleRequest[] = []
  let tracerCount = state.tracerCount
  let source: Readonly<{ weapon: number; owner: number; position: Vector3 }> | undefined
  const emit = (system: string, owner: number, weapon: number, points: readonly Vector3[], ordinal: number) => {
    if (!catalog.systems.has(system)) throw new ProjectilePresentationError("MissingSystem", system)
    const identity = `combat:${snapshot.tick}:${owner}:${weapon}:${ordinal}:${system}`
    const teamCode = owner === 1 ? snapshot.team : snapshot.bots.find(bot => bot.identity === owner)?.team
    if (teamCode !== 2 && teamCode !== 3) return
    particles.push(Object.freeze({
      kind: "start", identity, effectIdentity: identity, eventIdentity: identity,
      tick: snapshot.tick, projectileIdentity: (0x7f00_0000 + ordinal) >>> 0,
      ownerIdentity: owner, launcherIdentity: weapon, team: teamCode === 2 ? "red" : "blue",
      system, attachment: null,
      controlPoints: Object.freeze(points.map((position, index) => Object.freeze({
        index, position, orientation: Object.freeze([0, 0, 0, 1]) as Quaternion,
        ownerIdentity: owner,
      }))),
    }))
  }
  for (let ordinal = 0; ordinal < snapshot.events.length; ordinal += 1) {
    const event = snapshot.events[ordinal]!
    if (event.kind === 12) {
      source = Object.freeze({ weapon: event.detail, owner: event.auxiliary || 1,
        position: Object.freeze([event.values[0], event.values[1], event.values[2]]) as Vector3 })
      continue
    }
    if(event.kind===14&&event.subject!==1&&snapshot.bots.some(bot=>bot.identity===event.subject)){
      const hit=Object.freeze([event.values[0],event.values[1],event.values[2]]) as Vector3
      emit("blood_impact_red_01",event.subject,event.detail,Object.freeze([hit]),ordinal)
      const delta=hit.map((component,axis)=>component-snapshot.position[axis]!)
      emit(Math.hypot(...delta)<400?"blood_spray_red_01":"blood_spray_red_01_far",event.subject,event.detail,Object.freeze([hit]),ordinal)
      continue
    }
    if(event.kind===17&&event.auxiliary===1&&event.values[2]>0&&event.subject!==1){
      if(!catalog.playerActors)throw new ProjectilePresentationError("MalformedFact","critical feedback requires the current player roster")
      const actor=catalog.playerActors.get(event.subject)
      if(!actor)continue
      if([3,93].some(condition=>((actor.conditions[condition>>>5]??0)&(1<<(condition&31)))!==0))continue
      const head=catalog.playerAttachmentTransforms?.get(event.subject)?.get("head")
      if(!head)throw new ProjectilePresentationError("MissingAttachment",`${event.subject}:head`)
      emit(event.values[2]===1?"crit_text":"minicrit_text",event.subject,event.detail,Object.freeze([head.position]),ordinal)
      continue
    }
    if (event.kind !== 13 || !source || source.weapon !== event.detail) continue
    const hit = Object.freeze([event.values[0], event.values[1], event.values[2]]) as Vector3
    const critical = (event.auxiliary & 0x0001_0000) !== 0
    const frequency = source.weapon === 9 ? 4 : 2
    const showTracer = tracerCount++ % frequency === 0
    if (showTracer) {
      const stem = source.weapon === 4 ? "bullet_scattergun_tracer01"
        : source.weapon === 5 || source.weapon === 13 || source.weapon === 41 || source.weapon === 50
          ? "bullet_pistol_tracer01"
          : source.weapon === 7 || source.weapon === 10 || source.weapon === 40
            ? "bullet_shotgun_tracer01"
            : source.weapon === 9 ? "bullet_tracer01" : null
      const muzzle = source.owner === 1 ? catalog.attachmentTransforms.get(source.weapon)?.get("muzzle") : undefined
      if (stem && muzzle) {
        const team = snapshot.team === 2 ? "red" : "blue"
        emit(`${stem}_${team}${critical ? "_crit" : ""}`, source.owner, source.weapon,
          Object.freeze([muzzle.position, hit]), ordinal)
      }
    }
    if (event.subject !== 0 && event.subject !== 1 && (event.subject & 0x8000_0000) === 0) {
      emit("blood_impact_red_01", event.subject, source.weapon, Object.freeze([hit]), ordinal)
      const delta = hit.map((component, axis) => component - snapshot.position[axis]!)
      emit(Math.hypot(...delta) < 400 ? "blood_spray_red_01" : "blood_spray_red_01_far",
        event.subject, source.weapon, Object.freeze([hit]), ordinal)
    }
  }
  return Object.freeze({ particles: Object.freeze(particles), state: Object.freeze({ tracerCount }) })
}

export type ProjectileResourceCatalog = Readonly<{
  models: ReadonlySet<string>
  systems: ReadonlySet<string>
  attachments: ReadonlyMap<number, ReadonlySet<string>>
  attachmentTransforms: ReadonlyMap<number, ReadonlyMap<string, AttachmentTransform>>
  fireAttachmentTransforms: ReadonlyMap<number, ReadonlyMap<string, AttachmentTransform>>
  localOwnerIdentity?: number
}>

export type ProjectilePresentationLimits = Readonly<{
  maxProjectiles: number
  maxEvents: number
  maxRequests: number
}>

export class ProjectilePresentationError extends Error {
  constructor(
    readonly code:
      | "MalformedFact"
      | "MalformedEvent"
      | "IllegalTransition"
      | "MissingModel"
      | "MissingSystem"
      | "MissingAttachment"
      | "BoundExceeded"
      | "TimeReversed",
    message: string,
  ) {
    super(message)
    this.name = "ProjectilePresentationError"
  }
}

type TrackedProjectile = Readonly<{
  weapon: number
  miniRocket: boolean
  trail: number
  critical: boolean
  revision: number
  baseTrails: readonly string[]
  criticalTrail: string | null
  kind: ProjectileKind
  team: ProjectileTeam
  ownerIdentity: number
  launcherIdentity: number
  state: ProjectileState
  armed: boolean
  trailActive: boolean
  trailLocalTransform: AttachmentTransform | null
}>

type ProjectileSource = Readonly<{
  weapon: number
  critical: boolean
  trail: number
  miniRocket: boolean
  practiceExplosion: boolean
  selfBlastOnly: boolean
  identity: number
  kind: ProjectileKind
  team: ProjectileTeam
  ownerIdentity: number
  launcherIdentity: number
}>

const DEFAULT_LIMITS: ProjectilePresentationLimits = Object.freeze({
  maxProjectiles: 4_096,
  maxEvents: 4_096,
  maxRequests: 16_384,
})

export function createProjectilePresentationMapper(
  catalog: ProjectileResourceCatalog,
  limits: ProjectilePresentationLimits = DEFAULT_LIMITS,
): Readonly<{
  map(frame: ProjectileFrame): Readonly<{
    models: readonly ProjectileModelRequest[]
    particles: readonly ProjectileParticleRequest[]
    events: readonly ProjectileEvent[]
  }>
  reset(tick: bigint): void
  dispose(): void
}> {
  validateLimits(limits)
  let tick = 0n
  let hasMapped = false
  let tracked = new Map<number, TrackedProjectile>()
  let disposed = false

  return Object.freeze({
    map(frame: ProjectileFrame) {
      if (disposed) throw new ProjectilePresentationError("IllegalTransition", "mapper is disposed")
      const next = new Map(tracked)
      if (frame.ticks.length === 0) {
        throw new ProjectilePresentationError("MalformedFact", "projectile timeline is empty")
      }
      const particles: ProjectileParticleRequest[] = []
      const mappedEvents: ProjectileEvent[] = []
      let finalFacts = new Map<number, ProjectileFact>()
      let mappedTick = tick
      let totalEvents = 0
      for (let timelineIndex = 0; timelineIndex < frame.ticks.length; timelineIndex += 1) {
        const timeline = frame.ticks[timelineIndex]!
        if (
          typeof timeline.tick !== "bigint"
          || timeline.tick < mappedTick
          || ((hasMapped || timelineIndex > 0) && timeline.tick === mappedTick)
        ) {
          throw new ProjectilePresentationError("TimeReversed", "projectile timeline tick moved backward or repeated")
        }
        totalEvents += timeline.events.length
        if (timeline.projectiles.length > limits.maxProjectiles || totalEvents > limits.maxEvents) {
          throw new ProjectilePresentationError("BoundExceeded", "projectile timeline exceeds its count limit")
        }
        const facts = new Map<number, ProjectileFact>()
        for (const fact of timeline.projectiles) {
          validateFact(fact)
          if (facts.has(fact.identity)) {
            throw new ProjectilePresentationError("MalformedFact", "projectile identity is duplicated")
          }
          facts.set(fact.identity, fact)
        }
        const startedControls = new Map<number, ParticleControlPoint>()
        const lastDeflection = new Map<number, number>()
        timeline.events.forEach((event, index) => { if (event.kind === "deflect") lastDeflection.set(event.projectileIdentity, index) })
        for (let eventIndex = 0; eventIndex < timeline.events.length; eventIndex += 1) {
          const event = timeline.events[eventIndex]!
          const previousEvent = timeline.events[eventIndex - 1]
          validateEvent(event, timeline.tick, mappedTick, eventIndex)
          const fact = facts.get(event.projectileIdentity)
          const prior = next.get(event.projectileIdentity)
          const eventIdentity = `${event.tick}:${event.sourceEventOrdinal}:${event.kind}:${event.projectileIdentity}`
          if (event.kind === "fire") {
            if (prior) throw transition("fire targets an existing projectile")
            if (fact && (lastDeflection.get(event.projectileIdentity) ?? -1) <= eventIndex) matchEvent(event, fact)
            else if (fact && (event.projectileKind !== fact.kind || event.weapon !== fact.weapon)) throw transition("delayed fire immutable identity fields changed")
            const source = eventFact(event)
            const trail = trailSystem(source)
            requireSystem(catalog, trail)
            const trailAttachment = source.kind === "rocket"
              ? requireAttachment(catalog, source.identity, "trail")
              : null
            const trailWorldTransform = trailAttachment ? requireAttachmentTransform(catalog, trailAttachment) : null
            const retainedReference = frame.ticks.at(-1)?.projectiles.find((candidate) => candidate.identity === source.identity)
            const trailLocalTransform = trailWorldTransform
              ? localAttachmentTransform(
                  trailWorldTransform,
                  retainedReference?.position ?? event.position,
                  retainedReference?.orientation ?? event.orientation,
                )
              : null
            const initialTransform = trailLocalTransform
              ? applyAttachmentTransform(trailLocalTransform, event.position, event.orientation)
              : Object.freeze({ position: event.position, orientation: event.orientation })
            const trailStart = startRequest(
              eventIdentity,
              trailEffect(source.identity),
              source,
              trail,
              trailAttachment,
              initialTransform.position,
              initialTransform.orientation,
              event.tick,
            )
            push(particles, limits, trailStart)
            const baseTrails = [trailEffect(source.identity)]
            if (source.kind === "rocket" && source.trail === 3) {
              const effect = `${trailEffect(source.identity)}:line:0`
              requireSystem(catalog, "rockettrail_airstrike_line")
              push(particles, limits, startRequest(eventIdentity, effect, source, "rockettrail_airstrike_line", trailAttachment,
                initialTransform.position, initialTransform.orientation, event.tick))
              baseTrails.push(effect)
            }
            const criticalSystem = projectileCriticalSystem(source)
            const criticalTrail = criticalSystem ? `projectile:${source.identity}:critical:0` : null
            if (criticalTrail) {
              const system = criticalSystem!
              requireSystem(catalog, system)
              push(particles, limits, startRequest(eventIdentity, criticalTrail, source, system, null, event.position, event.orientation, event.tick))
            }
            startedControls.set(source.identity, trailStart.controlPoints[0]!)
            const muzzle = source.kind === "rocket" ? "rocketbackblast" : source.kind === "syringe" ? "muzzle_syringe"
              : source.kind === "flare" ? source.trail === 6 ? "drg_manmelter_muzzleflash" : "muzzle_shotgun" : source.kind === "grenade" ? "muzzle_grenadelauncher" : "muzzle_pipelauncher"
            if (!(source.kind === "rocket" && source.ownerIdentity === catalog.localOwnerIdentity)) {
              const attachment = requireAttachment(
                catalog,
                source.launcherIdentity,
                source.kind === "rocket" ? "backblast" : "muzzle",
              )
              const muzzleTransform = requireFireAttachmentTransform(catalog, event, attachment)
              requireSystem(catalog, muzzle)
              const muzzleRequest = startRequest(
                  eventIdentity,
                  oneShotEffect(eventIdentity),
                  source,
                  muzzle,
                  attachment,
                  muzzleTransform.position,
                  muzzleTransform.orientation,
                  event.tick,
                )
              push(particles, limits, muzzleRequest)
              if (source.kind === "flare") for (const color of weaponParticleColorRequests(muzzleRequest)) push(particles, limits, color)
            }
            next.set(
              source.identity,
              Object.freeze({
                weapon: source.weapon, miniRocket: source.miniRocket, trail: source.trail, critical: source.critical,
                revision: 0, baseTrails: Object.freeze(baseTrails), criticalTrail,
                kind: source.kind,
                team: source.team,
                ownerIdentity: source.ownerIdentity,
                launcherIdentity: source.launcherIdentity,
                state: "flying",
                armed: false,
                trailActive: true,
                trailLocalTransform,
              }),
            )
          } else {
            if (!prior) throw transition(`${event.kind} targets an unknown projectile`)
            if (fact && (lastDeflection.get(event.projectileIdentity) ?? -1) <= eventIndex) matchEvent(event, fact)
            if (event.kind !== "deflect") matchTracked(event, prior)
            if (event.kind === "deflect") {
              if (prior.kind !== event.projectileKind || prior.weapon !== event.weapon || !["rocket", "flare", "sticky", "grenade"].includes(prior.kind)) {
                throw transition("deflection changes the projectile class or original weapon")
              }
              const source = eventFact(event), revision = prior.revision + 1
              if(prior.kind === "sticky" && (prior.team !== source.team || prior.ownerIdentity !== source.ownerIdentity || prior.launcherIdentity !== source.launcherIdentity)) throw transition("sticky deflection changes ownership")
              const baseTrails = prior.kind === "rocket" ? [...prior.baseTrails] : []
              if (prior.kind !== "rocket") for (const effect of prior.baseTrails) {
                push(particles, limits, stopRequest(eventIdentity, effect, source.identity, event.tick, false))
              }
              if (prior.criticalTrail) push(particles, limits, stopRequest(eventIdentity, prior.criticalTrail, source.identity, event.tick, false))
              const attachment = source.kind === "rocket" ? requireAttachment(catalog, source.identity, "trail") : null
              const transform = prior.trailLocalTransform
                ? applyAttachmentTransform(prior.trailLocalTransform, event.position, event.orientation)
                : Object.freeze({ position: event.position, orientation: event.orientation })
              const effect = `${trailEffect(source.identity)}:${revision}`
              const system = trailSystem(source)
              requireSystem(catalog, system)
              push(particles, limits, startRequest(eventIdentity, effect, source, system, attachment, transform.position, transform.orientation, event.tick))
              baseTrails.push(effect)
              if (source.kind === "rocket" && source.trail === 3) {
                const line = `${effect}:line`
                requireSystem(catalog, "rockettrail_airstrike_line")
                push(particles, limits, startRequest(eventIdentity, line, source, "rockettrail_airstrike_line", attachment, transform.position, transform.orientation, event.tick))
                baseTrails.push(line)
              }
              const criticalSystem = projectileCriticalSystem(source)
              const criticalTrail = criticalSystem ? `projectile:${source.identity}:critical:${revision}` : null
              if (criticalTrail) {
                const system = criticalSystem!
                requireSystem(catalog, system)
                push(particles, limits, startRequest(eventIdentity, criticalTrail, source, system, null, event.position, event.orientation, event.tick))
              }
              next.set(source.identity, Object.freeze({ ...prior, team: source.team, ownerIdentity: source.ownerIdentity,
                launcherIdentity: source.launcherIdentity, trail: source.trail, critical: source.critical,
                revision, baseTrails: Object.freeze(baseTrails), criticalTrail }))
            } else if (event.kind === "impact") {
              if (prior.state !== "flying") throw transition("impact requires a flying projectile")
            } else if (event.kind === "bounce") {
              if (
                prior.state !== "flying"
                || previousEvent?.kind !== "impact"
                || previousEvent.projectileIdentity !== event.projectileIdentity
              ) {
                throw transition("bounce requires its immediately preceding flying impact")
              }
            } else if (event.kind === "stick") {
              if (
                prior.kind !== "sticky"
                || prior.state !== "flying"
                || previousEvent?.kind !== "impact"
                || previousEvent.projectileIdentity !== event.projectileIdentity
                || !fact
                || fact.state === "flying"
              ) {
                throw transition("stick requires a flying sticky and one settled fact")
              }
              next.set(event.projectileIdentity, Object.freeze({ ...prior, state: fact.state }))
            } else if (event.kind === "arm") {
              if (prior.kind !== "sticky" || prior.armed) throw transition("arm requires one unarmed sticky projectile")
              const system = prior.team === "red" ? "stickybomb_pulse_red" : "stickybomb_pulse_blue"
              requireSystem(catalog, system)
              push(
                particles,
                limits,
                startRequest(
                  eventIdentity,
                  pulseEffect(event.projectileIdentity),
                  eventFact(event),
                  system,
                  null,
                  event.position,
                  event.orientation,
                  event.tick,
                ),
              )
              next.set(event.projectileIdentity, Object.freeze({
                ...prior,
                state: fact?.state ?? prior.state,
                armed: true,
              }))
            } else if (event.kind === "fizzle" || event.kind === "explode") {
              if (prior.trailActive) {
                for (const effect of [...prior.baseTrails, ...(prior.criticalTrail ? [prior.criticalTrail] : [])]) {
                  push(particles, limits, stopRequest(eventIdentity, effect, event.projectileIdentity, event.tick, false))
                }
              }
              if (event.kind === "explode") {
                const system = event.projectileKind === "flare" ? "flaregun_destroyed"
                  : event.practiceExplosion ? "ExplosionCore_Wall_Jumper"
                    : event.underwaterExplosion ? "ExplosionCore_MidAir_underwater"
                      : event.contactNormal === null ? "ExplosionCore_MidAir" : "ExplosionCore_Wall"
                requireSystem(catalog, system)
                push(
                  particles,
                  limits,
                  startRequest(
                    eventIdentity,
                    oneShotEffect(eventIdentity),
                    eventFact(event),
                    system,
                    null,
                    event.position,
                    explosionOrientation(event.contactNormal),
                    event.tick,
                  ),
                )
                if (event.projectileKind === "flare" && event.airBurst) {
                  const extra = event.selfBlastOnly ? "Explosions_MA_FlyingEmbers" : "ExplosionCore_MidAir_Flare"
                  requireSystem(catalog, extra)
                  push(particles, limits, startRequest(eventIdentity, `${oneShotEffect(eventIdentity)}:air`, eventFact(event), extra,
                    null, event.position, explosionOrientation(event.contactNormal), event.tick))
                }
              }
            } else if (event.kind === "destroy") {
              if (
                (previousEvent?.kind !== "fizzle" && previousEvent?.kind !== "explode")
                || previousEvent.projectileIdentity !== event.projectileIdentity
              ) {
                throw transition("destroy requires its immediately preceding terminal event")
              }
              next.delete(event.projectileIdentity)
            }
          }
          mappedEvents.push(Object.freeze({
            ...event,
            position: vector(event.position),
            orientation: quat(event.orientation),
            contactNormal: event.contactNormal === null ? null : vector(event.contactNormal),
          }))
        }

        for (const [identity, state] of next) {
          const fact = facts.get(identity)
          if (!fact || fact.state !== state.state) {
            throw transition("projectile disappearance or state change has no ordered event")
          }
          if (
            fact.kind !== state.kind
            || fact.team !== state.team
            || fact.ownerIdentity !== state.ownerIdentity
            || fact.launcherIdentity !== state.launcherIdentity
            || fact.weapon !== state.weapon || fact.miniRocket !== state.miniRocket || fact.trail !== state.trail || fact.critical !== state.critical
          ) {
            throw transition("projectile immutable identity fields changed")
          }
          if (state.trailActive) {
            const transform = state.trailLocalTransform
              ? applyAttachmentTransform(state.trailLocalTransform, fact.position, fact.orientation)
              : Object.freeze({ position: fact.position, orientation: fact.orientation })
            const control = controlPoint(transform.position, transform.orientation, fact.ownerIdentity)
            const started = startedControls.get(identity)
            if (!started || !sameControlPoint(started, control)) {
              for (const effect of state.baseTrails) push(particles, limits, Object.freeze({
                  kind: "set-control-point",
                  identity: effect === trailEffect(identity) ? `${timeline.tick}:follow:${identity}` : `${timeline.tick}:follow:${identity}:${effect}`,
                  effectIdentity: effect,
                  eventIdentity: `${timeline.tick}:follow`,
                  tick: timeline.tick,
                  projectileIdentity: identity,
                  controlPoint: control,
                }))
            }
            if (state.criticalTrail) push(particles, limits, Object.freeze({
              kind: "set-control-point", identity: `${timeline.tick}:critical-follow:${identity}`,
              effectIdentity: state.criticalTrail, eventIdentity: `${timeline.tick}:follow`, tick: timeline.tick,
              projectileIdentity: identity, controlPoint: controlPoint(fact.position, fact.orientation, fact.ownerIdentity),
            }))
          }
        }
        for (const identity of facts.keys()) {
          if (!next.has(identity)) throw transition(`projectile fact ${identity} has no fire event after tick ${mappedTick}; tracked ${[...next.keys()].join(",")}`)
        }
        finalFacts = facts
        mappedTick = timeline.tick
      }

      const models = [...finalFacts.values()].filter((fact) => fact.modelVisible).map((fact) => {
        const model = projectileModelPath(fact.kind, fact.miniRocket)
        if (!catalog.models.has(model)) {
          throw new ProjectilePresentationError("MissingModel", `projectile model ${model} is missing`)
        }
        return Object.freeze({
          identity: fact.identity,
          projectileIdentity: fact.identity,
          model,
          skin: fact.kind === "rocket" || fact.team === "red" ? (0 as const) : (1 as const),
          materialVariant: fact.team,
          position: vector(fact.position),
          orientation: authoredProjectileOrientation(fact.orientation),
          angularVelocity: vector(fact.angularVelocity),
          state: fact.state,
        })
      })
      tracked = next
      tick = mappedTick
      hasMapped = true
      return Object.freeze({
        models: Object.freeze(models),
        particles: Object.freeze(particles),
        events: Object.freeze(mappedEvents),
      })
    },
    reset(nextTick: bigint): void {
      if (disposed || typeof nextTick !== "bigint" || nextTick < 0n) {
        throw new ProjectilePresentationError("TimeReversed", "projectile mapper reset tick is invalid")
      }
      tick = nextTick
      hasMapped = false
      tracked = new Map()
    },
    dispose(): void {
      disposed = true
      tracked.clear()
    },
  })
}

function validateFact(fact: ProjectileFact): void {
  if (
    !uint32(fact.identity) ||
    !uint32(fact.ownerIdentity) ||
    !uint32(fact.launcherIdentity) ||
    (fact.kind !== "rocket" && fact.kind !== "sticky" && fact.kind !== "syringe" && fact.kind !== "flare" && fact.kind !== "grenade") ||
    !Number.isInteger(fact.trail) || fact.trail < 0 || fact.trail > 6 || typeof fact.modelVisible !== "boolean" ||
    (fact.team !== "red" && fact.team !== "blue") ||
    (fact.state !== "flying" && fact.state !== "stuck-unarmed" && fact.state !== "stuck-armed") ||
    !finite(fact.position) ||
    !finite(fact.velocity) ||
    !quaternion(fact.orientation) ||
    !finite(fact.angularVelocity) ||
    !Number.isFinite(fact.ageSeconds) ||
    fact.ageSeconds < 0 ||
    (fact.contactNormal !== null && !normal(fact.contactNormal)) ||
    (fact.state === "flying" && fact.contactNormal !== null && fact.kind !== "flare") ||
    (fact.state !== "flying" && fact.contactNormal === null) ||
    (fact.kind === "rocket" && fact.state !== "flying")
  ) {
    throw new ProjectilePresentationError("MalformedFact", "projectile fact violates the frozen contract")
  }
}

function validateEvent(event: ProjectileEvent, frameTick: bigint, earliestTick: bigint, sourceOrdinal: number): void {
  if (
    !["fire", "impact", "bounce", "stick", "arm", "fizzle", "explode", "destroy", "deflect"].includes(event.kind) ||
    (event.projectileKind !== "rocket" && event.projectileKind !== "sticky" && event.projectileKind !== "syringe" && event.projectileKind !== "flare" && event.projectileKind !== "grenade") ||
    !Number.isInteger(event.trail) || event.trail < 0 || event.trail > 6 ||
    !uint32(event.projectileIdentity) ||
    !uint32(event.ownerIdentity) ||
    !uint32(event.launcherIdentity) ||
    (event.team !== "red" && event.team !== "blue") ||
    typeof event.tick !== "bigint" ||
    event.tick < earliestTick || event.tick > frameTick ||
    !Number.isSafeInteger(event.sourceOrdinal) ||
    event.sourceOrdinal !== sourceOrdinal ||
    !Number.isSafeInteger(event.sourceEventOrdinal) ||
    event.sourceEventOrdinal < 0 ||
    event.sourceEventOrdinal > event.sourceOrdinal ||
    !finite(event.position) ||
    !quaternion(event.orientation) ||
    (event.contactNormal !== null && !normal(event.contactNormal)) ||
    (event.kind === "fire" && event.contactNormal !== null) ||
    ((event.kind === "impact" || event.kind === "bounce" || event.kind === "stick") && event.contactNormal === null)
  ) {
    throw new ProjectilePresentationError("MalformedEvent", "projectile event violates the frozen contract")
  }
}

function matchEvent(event: ProjectileEvent, fact: ProjectileFact): void {
  if (
    event.ownerIdentity !== fact.ownerIdentity ||
    event.launcherIdentity !== fact.launcherIdentity ||
    event.team !== fact.team ||
    event.projectileKind !== fact.kind
  )
    throw transition("event identity fields do not match the projectile fact")
}

function matchTracked(event: ProjectileEvent, tracked: TrackedProjectile): void {
  if (
    event.ownerIdentity !== tracked.ownerIdentity ||
    event.launcherIdentity !== tracked.launcherIdentity ||
    event.team !== tracked.team ||
    event.projectileKind !== tracked.kind
  )
    throw transition("event identity fields do not match retained projectile state")
}

function startRequest(
  eventIdentity: string,
  effectIdentity: string,
  fact: ProjectileSource,
  system: string,
  attachment: ParticleAttachment | null,
  position: Vector3,
  orientation: Quaternion,
  tick: bigint,
): Extract<ProjectileParticleRequest, { kind: "start" }> {
  return Object.freeze({
    kind: "start",
    identity: `${eventIdentity}:start:${effectIdentity}`,
    effectIdentity,
    eventIdentity,
    tick,
    projectileIdentity: fact.identity,
    ownerIdentity: fact.ownerIdentity,
    launcherIdentity: fact.launcherIdentity,
    team: fact.team,
    system,
    attachment,
    controlPoints: Object.freeze([controlPoint(position, orientation, fact.ownerIdentity)]),
  })
}

function stopRequest(
  eventIdentity: string,
  effectIdentity: string,
  projectileIdentity: number,
  tick: bigint,
  immediate: boolean,
): Extract<ProjectileParticleRequest, { kind: "stop" }> {
  return Object.freeze({
    kind: "stop",
    identity: `${eventIdentity}:stop:${effectIdentity}`,
    effectIdentity,
    eventIdentity,
    tick,
    projectileIdentity,
    immediate,
  })
}

function controlPoint(position: Vector3, orientation: Quaternion, ownerIdentity: number): ParticleControlPoint {
  return Object.freeze({
    index: 0,
    position: vector(position),
    orientation: quat(orientation),
    ownerIdentity,
  })
}

function trailSystem(fact: ProjectileSource): string {
  if (fact.kind === "rocket") return fact.trail === 4 ? "rockettrail_underwater"
    : fact.trail === 1 ? "rockettrail_RocketJumper" : fact.trail === 2 || fact.trail === 3 ? "rockettrail_airstrike" : "rockettrail"
  if (fact.kind === "flare") return fact.trail === 6 ? "drg_manmelter_projectile"
    : `${fact.trail === 5 ? "scorchshot" : "flaregun"}_trail_${fact.critical ? "crit_" : ""}${fact.team}`
  if (fact.kind === "syringe") return fact.team === "red" ? "nailtrails_medic_red" : "nailtrails_medic_blue"
  if (fact.kind === "grenade") return `pipebombtrail_${fact.team}`
  return fact.team === "red" ? "stickybombtrail_red" : "stickybombtrail_blue"
}

function projectileCriticalSystem(fact:ProjectileSource):string|null {
  if(!fact.critical)return null
  if(fact.kind==="rocket")return `critical_rocket_${fact.team}`
  if(fact.kind==="sticky")return `critical_grenade_${fact.team}`
  if(fact.kind==="grenade")return `critical_pipe_${fact.team}`
  return null
}

function requireSystem(catalog: ProjectileResourceCatalog, system: string): void {
  if (!catalog.systems.has(system)) {
    throw new ProjectilePresentationError("MissingSystem", `particle system ${system} is missing`)
  }
}

function requireAttachment(
  catalog: ProjectileResourceCatalog,
  entityIdentity: number,
  name: ParticleAttachment["name"],
): ParticleAttachment {
  if (!catalog.attachments.get(entityIdentity)?.has(name)) {
    throw new ProjectilePresentationError("MissingAttachment", `attachment ${entityIdentity}:${name} is missing`)
  }
  return Object.freeze({ entityIdentity, name })
}

function requireAttachmentTransform(
  catalog: ProjectileResourceCatalog,
  attachment: ParticleAttachment,
): AttachmentTransform {
  const transform = catalog.attachmentTransforms.get(attachment.entityIdentity)?.get(attachment.name)
  return validateAttachmentTransform(transform, `${attachment.entityIdentity}:${attachment.name}`)
}

function requireFireAttachmentTransform(
  catalog: ProjectileResourceCatalog,
  event: ProjectileEvent,
  attachment: ParticleAttachment,
): AttachmentTransform {
  const transform = catalog.fireAttachmentTransforms.get(event.projectileIdentity)?.get(attachment.name)
  return validateAttachmentTransform(transform, `${event.tick}:${attachment.entityIdentity}:${attachment.name}`)
}

function validateAttachmentTransform(
  transform: AttachmentTransform | undefined,
  identity: string,
): AttachmentTransform {
  if (!transform) throw new ProjectilePresentationError("MissingAttachment", `attachment transform ${identity} is missing`)
  if (
    !finite(transform.position)
    || transform.position.some(value => !Number.isFinite(Math.fround(value)))
    || !quaternion(transform.orientation)
    || transform.orientation.some(value => !Number.isFinite(Math.fround(value)))
  ) {
    throw new ProjectilePresentationError("MalformedFact", `attachment transform ${identity} is invalid`)
  }
  return transform
}

function push(
  requests: ProjectileParticleRequest[],
  limits: ProjectilePresentationLimits,
  request: ProjectileParticleRequest,
): void {
  if (requests.length >= limits.maxRequests) {
    throw new ProjectilePresentationError("BoundExceeded", "particle request count exceeds its limit")
  }
  requests.push(request)
}

function authoredProjectileOrientation(orientation: Quaternion): Quaternion {
  // Source renders both projectile models at their transported entity quaternion.
  return quat(orientation)
}

function eventFact(event: ProjectileEvent): ProjectileSource {
  return Object.freeze({
    weapon: event.weapon, critical: event.critical, trail: event.trail, miniRocket: event.miniRocket,
    practiceExplosion: event.practiceExplosion, selfBlastOnly: event.selfBlastOnly,
    identity: event.projectileIdentity,
    kind: event.projectileKind,
    team: event.team,
    ownerIdentity: event.ownerIdentity,
    launcherIdentity: event.launcherIdentity,
  })
}

function explosionOrientation(contactNormal: Vector3 | null): Quaternion {
  if (contactNormal === null) return Object.freeze([0, 0, 0, 1])
  const [x, y, z] = contactNormal
  let pitch: number
  let yaw: number
  if (x === 0 && y === 0) {
    yaw = 0
    pitch = z > 0 ? 270 : 90
  } else {
    yaw = Math.atan2(y, x) * 180 / Math.PI
    if (yaw < 0) yaw += 360
    pitch = Math.atan2(-z, Math.sqrt(x * x + y * y)) * 180 / Math.PI
    if (pitch < 0) pitch += 360
  }
  return sourceViewOrientation(pitch, yaw)
}

function localAttachmentTransform(
  world: AttachmentTransform,
  position: Vector3,
  orientation: Quaternion,
): AttachmentTransform {
  const inverse = inverseQuaternion(orientation)
  return Object.freeze({
    position: rotate(inverse, subtract(world.position, position)),
    orientation: multiplyQuaternion(inverse, world.orientation),
  })
}

function applyAttachmentTransform(
  local: AttachmentTransform,
  position: Vector3,
  orientation: Quaternion,
): AttachmentTransform {
  return Object.freeze({
    position: add3(position, rotate(orientation, local.position)),
    orientation: multiplyQuaternion(orientation, local.orientation),
  })
}

function inverseQuaternion(value: Quaternion): Quaternion {
  return Object.freeze([-value[0], -value[1], -value[2], value[3]])
}

function sameControlPoint(left: ParticleControlPoint, right: ParticleControlPoint): boolean {
  return left.ownerIdentity === right.ownerIdentity
    && left.position.every((value, index) => value === right.position[index])
    && left.orientation.every((value, index) => value === right.orientation[index])
}

function quaternionFromBasis(x: Vector3, y: Vector3, z: Vector3): Quaternion {
  const m00 = x[0],
    m01 = y[0],
    m02 = z[0]
  const m10 = x[1],
    m11 = y[1],
    m12 = z[1]
  const m20 = x[2],
    m21 = y[2],
    m22 = z[2]
  const trace = m00 + m11 + m22
  let qx: number, qy: number, qz: number, qw: number
  if (trace > 0) {
    const s = Math.sqrt(trace + 1) * 2
    qw = 0.25 * s
    qx = (m21 - m12) / s
    qy = (m02 - m20) / s
    qz = (m10 - m01) / s
  } else if (m00 > m11 && m00 > m22) {
    const s = Math.sqrt(1 + m00 - m11 - m22) * 2
    qw = (m21 - m12) / s
    qx = 0.25 * s
    qy = (m01 + m10) / s
    qz = (m02 + m20) / s
  } else if (m11 > m22) {
    const s = Math.sqrt(1 + m11 - m00 - m22) * 2
    qw = (m02 - m20) / s
    qx = (m01 + m10) / s
    qy = 0.25 * s
    qz = (m12 + m21) / s
  } else {
    const s = Math.sqrt(1 + m22 - m00 - m11) * 2
    qw = (m10 - m01) / s
    qx = (m02 + m20) / s
    qy = (m12 + m21) / s
    qz = 0.25 * s
  }
  const length = Math.hypot(qx, qy, qz, qw)
  return Object.freeze([qx / length, qy / length, qz / length, qw / length]) as Quaternion
}

export function transformAttachment(
  matrix: Float32Array,
  position: Vector3,
  orientation: Quaternion,
): AttachmentTransform {
  if (matrix.length !== 12 || ![...matrix].every(Number.isFinite)) throw new ProjectilePresentationError("MalformedFact", "attachment matrix")
  const localPosition = vector([matrix[3]!, matrix[7]!, matrix[11]!])
  const localOrientation = quaternionFromBasis(
    vector([matrix[0]!, matrix[4]!, matrix[8]!]),
    vector([matrix[1]!, matrix[5]!, matrix[9]!]),
    vector([matrix[2]!, matrix[6]!, matrix[10]!]),
  )
  return Object.freeze({
    position: vector(add3(position, rotate(orientation, localPosition))),
    orientation: multiplyQuaternion(orientation, localOrientation),
  })
}

export function sourceViewOrientation(pitchDegrees: number, yawDegrees: number): Quaternion {
  const pitch = pitchDegrees * Math.PI / 180, yaw = yawDegrees * Math.PI / 180
  const cp = Math.cos(pitch), sp = Math.sin(pitch), cy = Math.cos(yaw), sy = Math.sin(yaw)
  return quaternionFromBasis(
    vector([cp * cy, cp * sy, -sp]),
    vector([-sy, cy, 0]),
    vector([sp * cy, sp * sy, cp]),
  )
}

function add3(left: Vector3, right: Vector3): Vector3 {
  return vector([left[0] + right[0], left[1] + right[1], left[2] + right[2]])
}

function multiplyQuaternion(left: Quaternion, right: Quaternion): Quaternion {
  return quat([
    left[3] * right[0] + left[0] * right[3] + left[1] * right[2] - left[2] * right[1],
    left[3] * right[1] - left[0] * right[2] + left[1] * right[3] + left[2] * right[0],
    left[3] * right[2] + left[0] * right[1] - left[1] * right[0] + left[2] * right[3],
    left[3] * right[3] - left[0] * right[0] - left[1] * right[1] - left[2] * right[2],
  ])
}

function rotate(quaternion: Quaternion, value: Vector3): Vector3 {
  const q = [quaternion[0], quaternion[1], quaternion[2]] as const
  const uv = cross(q, value)
  const uuv = cross(q, uv)
  return vector([
    value[0] + 2 * (uv[0] * quaternion[3] + uuv[0]),
    value[1] + 2 * (uv[1] * quaternion[3] + uuv[1]),
    value[2] + 2 * (uv[2] * quaternion[3] + uuv[2]),
  ])
}

function transition(message: string): ProjectilePresentationError {
  return new ProjectilePresentationError("IllegalTransition", message)
}

function validateLimits(limits: ProjectilePresentationLimits): void {
  if (
    !positiveInteger(limits.maxProjectiles) ||
    !positiveInteger(limits.maxEvents) ||
    !positiveInteger(limits.maxRequests)
  ) {
    throw new ProjectilePresentationError("BoundExceeded", "projectile presentation limits must be positive integers")
  }
}

function uint32(value: number): boolean {
  return Number.isInteger(value) && value > 0 && value <= UINT32_MAX
}

function positiveInteger(value: number): boolean {
  return Number.isSafeInteger(value) && value > 0
}

function finite(value: readonly number[]): boolean {
  return value.length === 3 && value.every(Number.isFinite)
}

function quaternion(value: readonly number[]): boolean {
  return value.length === 4 && value.every(Number.isFinite) && Math.abs(lengthSquared4(value) - 1) <= NORMAL_TOLERANCE
}

function normal(value: readonly number[]): boolean {
  return finite(value) && Math.abs(lengthSquared(value as Vector3) - 1) <= NORMAL_TOLERANCE
}

function vector(value: readonly number[]): Vector3 {
  return Object.freeze([value[0]!, value[1]!, value[2]!])
}

function quat(value: readonly number[]): Quaternion {
  return Object.freeze([value[0]!, value[1]!, value[2]!, value[3]!])
}

function subtract(left: Vector3, right: Vector3): Vector3 {
  return vector([left[0] - right[0], left[1] - right[1], left[2] - right[2]])
}

function cross(left: Vector3, right: Vector3): Vector3 {
  return vector([
    left[1] * right[2] - left[2] * right[1],
    left[2] * right[0] - left[0] * right[2],
    left[0] * right[1] - left[1] * right[0],
  ])
}

function dot(left: Vector3, right: Vector3): number {
  return left[0] * right[0] + left[1] * right[1] + left[2] * right[2]
}

function lengthSquared(value: Vector3): number {
  return dot(value, value)
}

function lengthSquared4(value: readonly number[]): number {
  return value.reduce((sum, component) => sum + component * component, 0)
}

function trailEffect(identity: number): string {
  return `projectile:${identity}:trail`
}

function pulseEffect(identity: number): string {
  return `projectile:${identity}:pulse`
}

function oneShotEffect(eventIdentity: string): string {
  return `event:${eventIdentity}`
}
